import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { useSession } from '@/lib/SessionContext';
import { canManagePO, checkIsDev, checkIsOwner, getPageAccess } from '@/lib/AuthSession';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Edit2, Trash2, Save, CheckCircle2, Truck, Lock, Plus, FileText, Tag, Printer,
  FlaskConical, Leaf, CircleDot, Wind, Droplets, Package, PackageCheck, ChevronDown, ChevronUp, UserCircle2, Sparkles, RotateCcw, User, ShieldCheck, ShieldAlert, Zap, Clock, Search, X, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ProgressRing from '@/components/production/ProgressRing';
import StatusBadge from '@/components/production/StatusBadge';
import { smartDisplayQty } from '@/lib/unitConverter';
import POComponentItem from '@/components/production/POComponentItem';
import AdditionalComponentSection from '@/components/production/AdditionalComponentSection';
import NeedBuySection from '@/components/production/NeedBuySection';
import RacikanFormulaSection from '@/components/production/RacikanFormulaSection';
import SelesaiKirimModal from '@/components/production/SelesaiKirimModal';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';
import LabelDusPrintModal from '@/Components/production/LabelDusPrintModal';
import AsaPrintChoiceModal from '@/Components/production/AsaPrintChoiceModal';
import CetakRuahanModal from '@/Components/production/CetakRuahanModal';
import TambahTugasSection from '@/components/production/TambahTugasSection';
import QCAuditModal from '@/components/production/QCAuditModal';
import MaserasiTimer from '@/components/production/MaserasiTimer';
import { createFillingTaskIfNeeded } from '@/lib/productionTaskPipeline';
import ClickToPreviewImage from '@/components/ui/ClickToPreviewImage';
import { deductStockForPO, previewStockDeduction } from '@/lib/stockDeduction';
import AsaStockDeductionModal from '@/Components/shared/AsaStockDeductionModal';
import { generateSpkPdf } from '@/lib/spkGenerator';
import SpkPrintModal from '@/components/finance/SpkPrintModal';
import { logActivity } from '@/utils/activityLogger';
const CORE_CATEGORIES = ['botol', 'bibit', 'tutup', 'spray', 'bahan_cair', 'tambahan'];

const makeComp = (category) => ({
  category,
  inventory_item_id: '',
  item_name: '',
  vendor: '',
  qty_needed: 0,
  stock_available: 0,
  unit: 'pcs',
  status: 'pending',
  item_notes: '',
  is_integrated: true,
  color_code: '',
  target_qty: 0,
  current_qty: 0,
  name: '',
});

// Ambil hanya komponen core (botol/bibit/tutup/spray) dari data DB
// Komponen lainnya diabaikan
function parseCoreComponents(components) {
  const list = components || [];
  return CORE_CATEGORIES.map(cat => {
    const forCat = list.filter(c => c.category === cat && (c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name));
    if (forCat.length > 0) {
      return forCat.map(c => ({ ...makeComp(cat), ...c, category: cat, is_integrated: true }));
    }
    return ['botol', 'tutup', 'spray', 'bibit'].includes(cat) ? [makeComp(cat)] : [];
  });
}

export default function ProductionDetail() {
  const id = window.location.pathname.split('/').pop();
  const { member, companySettings: globalCompanySettings } = useSession();
  const userRole = (member?.role || '').toLowerCase().trim();
  const userPos = (member?.position || '').toLowerCase().trim();
  const userDiv = (member?.division || member?.divisi || member?.department || '').toLowerCase().trim();
  const isQc = userRole.includes('qc') || userPos.includes('qc') || userDiv.includes('qc');
  const isKepalaOrLeader = 
    userRole.includes('kepala') || 
    userPos.includes('kepala') || 
    userRole.includes('supervisor') || 
    userPos.includes('supervisor') || 
    userRole.includes('leader') ||
    userPos.includes('leader') ||
    userRole.includes('manager') ||
    userPos.includes('manager');
  const isProduksiOrPengemasan = 
    userDiv.includes('produksi') || 
    userRole.includes('produksi') || 
    userPos.includes('produksi') || 
    userRole.includes('pengemasan') || 
    userPos.includes('pengemasan') ||
    String(member?.tipe_produksi || '').toLowerCase().includes('sekunder') ||
    String(member?.tipe_produksi || '').toLowerCase().includes('primer');

  const canEdit = 
    canManagePO(member) || 
    checkIsDev(member) || 
    checkIsOwner(member) || 
    isQc ||
    getPageAccess(member, "po_produksi") === "edit" ||
    getPageAccess(member, "produksi") === "edit" ||
    ['developer', 'owner', 'direktur', 'admin', 'kepala_produksi', 'kepala_produksi_primer', 'kepala_produksi_sekunder', 'kepala_pengemasan', 'qc', 'quality_control', 'kepala_qc', 'staff_qc'].includes(userRole.replace(/\s+/g, '_')) ||
    ['developer', 'owner', 'direktur', 'admin', 'kepala_produksi', 'kepala_produksi_primer', 'kepala_produksi_sekunder', 'kepala_pengemasan', 'qc', 'quality_control', 'kepala_qc', 'staff_qc'].includes(userPos.replace(/\s+/g, '_')) ||
    (isKepalaOrLeader && isProduksiOrPengemasan);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [markingDone, setMarkingDone] = useState(false);
  const [showSelesaiModal, setShowSelesaiModal] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [suratJalanToPrint, setSuratJalanToPrint] = useState(null);
  const [showHapusSuratJalanModal, setShowHapusSuratJalanModal] = useState(false);
  const [labelDusToPrint, setLabelDusToPrint] = useState(null);
  const [asaPrintTarget, setAsaPrintTarget] = useState(null);
  const [showAsaConfirm, setShowAsaConfirm] = useState(false);
  const [editingVariantIdx, setEditingVariantIdx] = useState(null);
  const [coreComponents, setCoreComponents] = useState(null);
  const [variantEditForm, setVariantEditForm] = useState(null);
  const [addons, setAddons] = useState([]);
  const [expandedVariant, setExpandedVariant] = useState(null);

  const { data: settingsList = [] } = useQuery({
    queryKey: ['company-settings'],
    queryFn: () => base44.entities.CompanySettings.list(),
  });
  const localSettings = settingsList[0] || {};
  const companySettings = (localSettings.logo_url || localSettings.stempel_url || localSettings.ttd_url) ? localSettings : { ...globalCompanySettings, ...localSettings };

  const [qcModalState, setQcModalState] = useState({ isOpen: false, variant: null, completedPacking: 0 });
  const [showSpkModal, setShowSpkModal] = useState(false);
  const [asaStockModal, setAsaStockModal] = useState(null);
  const [isCetakRuahanOpen, setIsCetakRuahanOpen] = useState(false);
  const [variantSearch, setVariantSearch] = useState('');
  const [variantStatusFilter, setVariantStatusFilter] = useState('all'); // 'all', 'waiting', 'in_progress', 'done'

  const { data: order, isLoading } = useQuery({
    queryKey: ['production-order', id],
    queryFn: async () => {
      const orders = await base44.entities.ProductionOrder.filter({ id });
      const rawOrder = orders[0];
      if (rawOrder && Array.isArray(rawOrder.products)) {
        rawOrder.products = rawOrder.products.map(p => {
          const vName = p.product_name || p.nama_product || p.variant_name || p.nama_varian || (p.variant_id ? p.variant_id.replace(/^.*?\s*-\s*/, '') : '') || '';
          return {
            ...p,
            product_name: p.product_name || vName,
            nama_product: p.nama_product || vName,
            variant_name: p.variant_name || vName,
          };
        });
      }
      return rawOrder;
    },
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const allVariantsPassedQC = useMemo(() => {
    if (!order) return true;
    if (['siap_kirim', 'shipping', 'dalam_pengiriman', 'done'].includes(order.status)) return true;
    if (!order?.products?.length) return true;
    return order.products.every(p => (p.qc_data && ['passed', 'passed_with_rejects'].includes(p.qc_data.status)) || ['siap_kirim', 'shipping', 'done'].includes(order.status));
  }, [order]);

  const isDirectCompletion = useMemo(() => {
    if (!order) return false;
    // Hanya tampil jika status order memang 'done'. Jika dibalikkan ke in_progress / status lain, badge langsung hilang!
    if (order.status !== 'done') return false;
    if (order.diselesaikan_langsung === true || order.completion_type === 'direct') return true;
    if (!order.task_stock_deducted || (!order.task_stock_deducted.racik && !order.task_stock_deducted.filling)) {
      return true;
    }
    return false;
  }, [order]);

  const { data: linkedInvoice } = useQuery({
    queryKey: ['client-po', order?.order_number],
    queryFn: async () => {
      if (!order?.order_number) return null;
      const invs = await base44.entities.ClientPO.filter({ id: order.order_number });
      return invs[0] || null;
    },
    enabled: !!order?.order_number,
  });

  const spkInvoiceData = useMemo(() => {
    if (linkedInvoice) {
      return {
        ...linkedInvoice,
        brand_name: linkedInvoice.brand_name || order?.brand_name,
        pic_marketing: linkedInvoice.pic_brand || linkedInvoice.pic || order?.pic_marketing,
        pic: linkedInvoice.pic_brand || linkedInvoice.pic || order?.pic_marketing,
        order_number: order?.order_number || linkedInvoice.id,
        id: linkedInvoice.id || order?.id,
        spk_printed_by: order?.spk_printed_by || linkedInvoice.spk_printed_by,
        spk_printed_date: order?.spk_printed_date || linkedInvoice.spk_printed_date,
        spk_printed_at: order?.spk_printed_at || linkedInvoice.spk_printed_at,
        components: order?.components || linkedInvoice.components,
        racikan_digunakan: order?.racikan_digunakan || linkedInvoice.racikan_digunakan,
      };
    }
    return order;
  }, [linkedInvoice, order]);

  const { data: rawInventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list('item_name'),
  });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: bahanList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: databaseBahanList = [] } = useQuery({ queryKey: ['database-bahan'], queryFn: () => base44.entities.DatabaseBahan.list('nama_brand') });
  const { data: racikanDbList = [] } = useQuery({ queryKey: ['racikan-database'], queryFn: () => base44.entities.RacikanDatabase.list('nama_brand') });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  // Gabungkan stok & inventori ke format InventoryItem
  const inventoryItems = useMemo(() => {
    const fromStok = [
      ...botolList.map(i => ({ id: `botol-${i.id}`, _stokId: i.id, _entity: 'Botol', item_name: i.nama, category: 'botol', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan || '', minimum_stock: 10 })),
      ...tutupList.map(i => ({ id: `tutup-${i.id}`, _stokId: i.id, _entity: 'Tutup', item_name: i.nama, category: 'tutup', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan || '', minimum_stock: 10 })),
      ...sprayList.map(i => ({ id: `spray-${i.id}`, _stokId: i.id, _entity: 'Spray', item_name: i.nama, category: 'spray', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.tipe || '', minimum_stock: 10 })),
      ...bahanList.map(i => ({ id: `bahan-${i.id}`, _stokId: i.id, _entity: 'BahanCair', item_name: i.nama, category: i.kategori === 'Bibit' ? 'bibit' : 'bahan', kategori_asli: i.kategori, total_stock: i.stok ?? 0, unit: i.satuan || 'kg', vendor: i.vendor || '', minimum_stock: 0.5 })),
      ...fullsetList.map(i => ({ id: `fullset-${i.id}`, _stokId: i.id, _entity: 'Fullset', item_name: i.nama, category: 'botol', total_stock: i.stok ?? 0, unit: 'pcs', vendor: i.catatan || '', minimum_stock: 10, is_fullset: true })),
    ];
    // Merge: prioritaskan stok, tambahkan rawInventory yang tidak duplikat
    const stokIds = new Set(fromStok.map(i => i.item_name?.toLowerCase()));
    const onlyRaw = rawInventory.filter(i => !stokIds.has(i.item_name?.toLowerCase()));
    return [...fromStok, ...onlyRaw];
  }, [botolList, tutupList, sprayList, bahanList, fullsetList, rawInventory]);

  // Sync coreComponents setiap kali data order berubah dari server (hard re-fetch)
  // Jangan sync saat sedang dalam mode edit agar input user tidak terhapus
  useEffect(() => {
    if (order && editingVariantIdx === null) {
      setCoreComponents(parseCoreComponents(order.components));
      setAddons(order.additional_components || []);
    }
  }, [order, editingVariantIdx]);

  // Load BarangMasuk untuk sinkronisasi otomatis
  const { data: barangMasukList = [] } = useQuery({
    queryKey: ['barang-masuk'],
    queryFn: () => base44.entities.BarangMasuk.list('-created_date'),
  });

  // Auto-sinkron purchase statuses DIHAPUS — status hanya berubah manual oleh user


  const stockMap = useMemo(() => {
    const map = {};
    inventoryItems.forEach(item => {
      if (item.id) map[item.id] = item.total_stock || 0;
      if (item._stokId) map[item._stokId] = item.total_stock || 0;
      if (item.item_name) map[item.item_name.toLowerCase().trim()] = item.total_stock || 0;
    });
    return map;
  }, [inventoryItems]);

  // Enrich dengan stok real-time + auto-sinkron dari BarangMasuk
  const enrichedCore = useMemo(() => {
    if (!coreComponents) return CORE_CATEGORIES.map(() => []);

    // Buat peta nama barang → total qty yang sudah masuk dari BarangMasuk
    const arrivedMap = {}; // { nama_barang_lower: total_jumlah }
    barangMasukList.forEach(bm => {
      (bm.items || []).forEach(item => {
        const key = (item.nama_barang || '').toLowerCase().trim();
        if (key) arrivedMap[key] = (arrivedMap[key] || 0) + (item.jumlah || 0);
      });
    });

    const runningUsage = {}; // Track penggunaan akumulatif

    return coreComponents.map(catComps =>
      catComps.map(comp => {
        const invId = comp.inventory_item_id;
        const nameKey = (comp.item_name || '').toLowerCase().trim();
        const rawStock = (invId && stockMap[invId] !== undefined)
          ? stockMap[invId]
          : (nameKey && stockMap[nameKey] !== undefined)
            ? stockMap[nameKey]
            : (comp.stock_available ?? 0);
        const usageKey = invId || nameKey || 'unknown';
        const priorUsage = runningUsage[usageKey] || 0;
        const realStock = Math.max(0, rawStock - priorUsage);
        const needed = comp.qty_needed || 0;

        // Catat penggunaan untuk variant/komponen selanjutnya
        if (needed > 0) {
          runningUsage[usageKey] = priorUsage + needed;
        }

        // Cek apakah ada di BarangMasuk (barang sudah datang)
        const arrivedQty = arrivedMap[nameKey] || 0;
        const isFromClient = comp.vendor === 'Client' || comp.dari_client;

        let status = 'pending';
        if (comp.status === 'pending_client') {
          status = 'pending_client';
        } else if (comp.status === 'ready_client') {
          status = 'ready_client';
        } else if (needed > 0 && realStock < needed) {
          status = isFromClient ? 'pending_client' : 'need_buy';
        } else if (needed > 0 && realStock >= needed) {
          status = isFromClient ? 'ready_client' : 'ready';
        } else if (realStock <= 0) {
          status = isFromClient ? 'pending_client' : 'need_buy';
        }

        return { ...comp, stock_available: realStock, status, arrived_qty: arrivedQty };
      })
    );
  }, [coreComponents, stockMap, barangMasukList]);

  const { data: allTasks = [] } = useQuery({
    queryKey: ["production-tasks", order?.id],
    queryFn: async () => {
      if (!order?.id) return [];
      return await base44.entities.ProductionTask.filter({ po_id: order.id });
    },
    enabled: !!order?.id
  });

  // Auto-complete any remaining tasks if the PO is already marked as done / diselesaikan langsung
  useEffect(() => {
    if (!order || (order.status !== 'done' && !order.diselesaikan_langsung && order.completion_type !== 'direct')) return;
    if (!allTasks || allTasks.length === 0) return;

    const pendingTasks = allTasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
    if (pendingTasks.length > 0) {
      const nowIso = new Date().toISOString();
      pendingTasks.forEach(async (task) => {
        try {
          const targetQty = Number(task.target_qty || task.qty || 0);
          let updatedMemberProgress = task.member_progress;
          if (task.member_progress && typeof task.member_progress === 'object') {
            updatedMemberProgress = { ...task.member_progress };
            Object.keys(updatedMemberProgress).forEach(memberKey => {
              const prog = updatedMemberProgress[memberKey];
              if (prog && prog.status !== 'completed') {
                updatedMemberProgress[memberKey] = {
                  ...prog,
                  status: 'completed',
                  completed_at: prog.completed_at || nowIso,
                  completed_qty: prog.completed_qty || prog.target_qty || targetQty,
                };
              }
            });
          }

          await base44.entities.ProductionTask.update(task.id, {
            status: 'completed',
            end_time: task.end_time || nowIso,
            member_progress: updatedMemberProgress,
            notes: task.notes ? `${task.notes} | Diselesaikan otomatis (PO Selesai)` : 'Diselesaikan otomatis (PO Selesai)'
          });
          queryClient.invalidateQueries({ queryKey: ["production-tasks", order.id] });
          queryClient.invalidateQueries({ queryKey: ["production-tasks"] });
        } catch (e) {
          console.warn('Auto-completing task failed:', e);
        }
      });
    }
  }, [order, allTasks, queryClient]);

  const getProductDisplayName = (p) => {
    let name = (p.product_name || p.variant_name || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
    const size = p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml;
    if (size) name += ` (${size}ml)`;
    return name;
  };

  const getVariantStatus = (prod) => {
    const productName = prod.product_name || prod.variant_name;

    // Check shipped status first
    if (order?.status === 'shipping' || order?.status === 'done' || order?.is_shipped) {
      const vName = getProductDisplayName(prod);

      let totalShipped = 0;
      (order.shipments || []).forEach(shipment => {
        (shipment.items || []).forEach(item => {
          if (item.variant_name === vName) {
            totalShipped += Number(item.qty_akhir) || 0;
          }
        });
      });

      const targetQty = Number(prod.qty) || Number(prod.target_qty) || 0;

      if (totalShipped > 0) {
        if (totalShipped >= targetQty) {
          return { label: 'Sudah Dikirim', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', category: 'done', stage: 'done' };
        } else {
          return { label: `Dikirim Sebagian (${totalShipped}/${targetQty})`, color: 'bg-teal-50 text-teal-700 border-teal-200', category: 'in_progress', stage: 'shipping_partial' };
        }
      }
    }

    // Include both variant-specific tasks AND global tasks (product_name is null/undefined)
    const pTasks = allTasks.filter(t => {
      const matchName = getProductDisplayName(prod);
      const cleanProdName = (productName || '').toLowerCase().trim();
      const cleanMatchName = matchName.toLowerCase().trim();
      const tProd = (t.product_name || '').toLowerCase().trim();
      const tTask = (t.task_name || '').toLowerCase().trim();
      
      // 1. Jika product_name atau task_name cocok dengan nama varian
      if (tProd === cleanProdName || tProd === cleanMatchName) return true;
      if (tTask.includes(cleanProdName) || tTask.includes(cleanMatchName)) return true;

      // 2. Cek apakah task menggunakan nama aroma/bibit racikan dari varian ini
      const racikanBahan = (prod.racikan_digunakan || prod.racikan || []).map(r => (r.nama_bahan || '').toLowerCase().trim());
      const hasBibitMatch = racikanBahan.some(bahan => {
        if (!bahan) return false;
        const mainBibitWords = bahan.split(/\s+/).filter(w => w.length > 3 && !/^\d+$/.test(w) && !['parf', 'plus', 'mkt', 'oil'].includes(w));
        return mainBibitWords.length > 0 && mainBibitWords.some(w => tProd.includes(w) || tTask.includes(w));
      });
      if (hasBibitMatch) return true;
      
      // 3. Jika variants array ada, cek apakah variant ini termasuk
      if (t.variants && t.variants.length > 0) {
         return t.variants.some(v => (v || '').toLowerCase().trim() === cleanMatchName || (v || '').toLowerCase().trim() === cleanProdName);
      }
      // 4. Jika tidak ada product_name dan tidak ada variants, berarti ini global task (berlaku untuk semua)
      if (!t.product_name && (!t.variants || t.variants.length === 0)) return true;
      
      return false;
    });

    if (pTasks.length === 0) {
      if (['siap_kirim', 'done', 'dalam_pengiriman', 'shipping'].includes(order?.status)) {
        return { label: 'Selesai Dikemas', color: 'bg-green-100 text-green-700 border-green-200', category: 'done', stage: 'done' };
      }
      if (order?.status === 'packing') {
        return { label: 'Belum Ada Tugas Kemas', color: 'bg-slate-100 text-slate-700 border-slate-200', category: 'waiting', stage: 'waiting' };
      }
      if (order?.status === 'filling') {
        return { label: 'Belum Ada Tugas Filling', color: 'bg-slate-100 text-slate-700 border-slate-200', category: 'waiting', stage: 'waiting' };
      }
      return { label: 'Belum Dikerjakan', color: 'bg-slate-100 text-slate-700 border-slate-200', category: 'waiting', stage: 'waiting' };
    }

    // Identify tasks by activity stage
    const packingTasks = pTasks.filter(t => ['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes((t.activity_type || '').toLowerCase()) || (t.task_name && t.task_name.toLowerCase().includes('pack')));
    const fillingTasks = pTasks.filter(t => ['filling'].includes((t.activity_type || '').toLowerCase()) || (t.task_name && t.task_name.toLowerCase().includes('filling')));
    const mixingTasks = pTasks.filter(t => ['racik', 'mixing'].includes((t.activity_type || '').toLowerCase()) || (t.task_name && (t.task_name.toLowerCase().includes('mixing') || t.task_name.toLowerCase().includes('racik'))));

    // 1. Cek Tahap Pengemasan
    if (packingTasks.length > 0) {
      const allPackingDone = packingTasks.every(t => ['completed', 'cancelled'].includes(t.status));
      if (allPackingDone) {
        return { label: 'Selesai Dikemas', color: 'bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold', category: 'done', stage: 'done' };
      }
      const inProgressPack = packingTasks.find(t => t.status === 'in_progress');
      if (inProgressPack) {
        return { label: 'Sedang Dikemas', color: 'bg-amber-100 text-amber-800 border-amber-300 font-semibold', category: 'in_progress', stage: 'packing' };
      }
      return { label: 'Menunggu Pengemasan', color: 'bg-blue-100 text-blue-800 border-blue-300 font-semibold', category: 'in_progress', stage: 'packing' };
    }

    // 2. Cek Tahap Filling
    if (fillingTasks.length > 0) {
      const allFillingDone = fillingTasks.every(t => ['completed', 'cancelled'].includes(t.status));
      if (allFillingDone) {
        return { label: 'Selesai Filling (Siap Dikemas)', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 font-medium', category: 'in_progress', stage: 'filling_done' };
      }
      const inProgressFill = fillingTasks.find(t => t.status === 'in_progress');
      if (inProgressFill) {
        return { label: 'Sedang Filling', color: 'bg-amber-100 text-amber-800 border-amber-300 font-semibold', category: 'in_progress', stage: 'filling' };
      }
      return { label: 'Menunggu Filling', color: 'bg-blue-100 text-blue-800 border-blue-300 font-semibold', category: 'in_progress', stage: 'filling' };
    }

    // 3. Cek Tahap Racik / Mixing
    if (mixingTasks.length > 0) {
      const allMixingDone = mixingTasks.every(t => ['completed', 'cancelled'].includes(t.status));
      if (allMixingDone) {
        if (order?.status === 'maserasi') {
          return { label: 'Sedang Maserasi', color: 'bg-purple-100 text-purple-700 border-purple-200 font-semibold', category: 'in_progress', stage: 'maserasi' };
        }
        return { label: 'Selesai Diracik (Siap Filling)', color: 'bg-indigo-50 text-indigo-700 border-indigo-200 font-medium', category: 'in_progress', stage: 'racik_done' };
      }
      const inProgressMix = mixingTasks.find(t => t.status === 'in_progress');
      if (inProgressMix) {
        return { label: 'Sedang Diracik', color: 'bg-amber-100 text-amber-800 border-amber-300 font-semibold', category: 'in_progress', stage: 'racik' };
      }
      return { label: 'Menunggu Diracik', color: 'bg-blue-100 text-blue-800 border-blue-300 font-semibold', category: 'in_progress', stage: 'racik' };
    }

    // 4. Other tasks (QC, etc.)
    const inProgress = pTasks.find(t => t.status === 'in_progress');
    if (inProgress) return { label: `Mengerjakan ${inProgress.activity_type || inProgress.task_name}`, color: 'bg-amber-100 text-amber-700 border-amber-200', category: 'in_progress', stage: 'other' };

    const assigned = pTasks.find(t => t.status === 'assigned');
    if (assigned) return { label: `Menunggu ${assigned.activity_type || assigned.task_name}`, color: 'bg-blue-100 text-blue-700 border-blue-200', category: 'in_progress', stage: 'other' };

    const allDone = pTasks.length > 0 && pTasks.every(t => ['completed', 'cancelled'].includes(t.status));
    if (allDone) {
      if (['siap_kirim', 'done', 'dalam_pengiriman', 'shipping'].includes(order?.status)) {
        return { label: 'Selesai Dikemas', color: 'bg-green-100 text-green-700 border-green-200', category: 'done', stage: 'done' };
      }
      return { label: 'Tugas Selesai', color: 'bg-green-50 text-green-700 border-green-200', category: 'in_progress', stage: 'done' };
    }

    return { label: 'Dalam Proses', color: 'bg-amber-100 text-amber-700 border-amber-200', category: 'in_progress', stage: 'in_progress' };
  };

  // Enrich per variant untuk Accordion
  const enrichedProducts = useMemo(() => {
    if (!order?.products?.length) return [];

    const arrivedMap = {};
    barangMasukList.forEach(bm => {
      (bm.items || []).forEach(item => {
        const key = (item.nama_barang || '').toLowerCase().trim();
        if (key) arrivedMap[key] = (arrivedMap[key] || 0) + (item.jumlah || 0);
      });
    });

    const runningUsage = {};

    return order.products.map((prod, originalIndex) => {
      const prodTargetQty = Number(prod.target_qty || prod.qty || 0);
      const prodBotolSize = Number(prod.botol_ukuran_aktual || prod.botol_ukuran_label || prod.botol_ukuran_client || prod.ukuran_botol_ml || 10);
      let racikanList = prod.racikan_digunakan || prod.racikan || [];
      if (!racikanList.length) {
        const vClean = (prod.product_name || prod.variant_name || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim().toLowerCase();
        const bBrand = (order?.brand_name || '').toLowerCase().trim();
        const matchedBahan = databaseBahanList.find(b => (b.nama_varian || '').toLowerCase().trim() === vClean && (!b.nama_brand || bBrand.includes(b.nama_brand.toLowerCase().trim())));
        if (matchedBahan?.racikan?.length) racikanList = matchedBahan.racikan;
        else {
          const matchedDb = racikanDbList.find(db => (db.nama_varian || '').toLowerCase().trim() === vClean && (!db.nama_brand || bBrand.includes(db.nama_brand.toLowerCase().trim())));
          if (matchedDb?.racikan?.length) racikanList = matchedDb.racikan;
          else if (order?.products?.length === 1 && order.racikan_digunakan?.length) {
            racikanList = order.racikan_digunakan;
          }
        }
      }

      const variantCore = CORE_CATEGORIES.map(cat => {
        const sourceComps = (prod.components && prod.components.length > 0) ? prod.components : (order.components || []);
        const catComps = sourceComps.filter(c => c.category === cat && (c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name || c.name));

        return catComps.map(comp => {
          const invId = comp.inventory_item_id;
          const namaKey = (comp.item_name || comp.name || '').toLowerCase().trim();
          const cleanKey = namaKey.replace(/\s+/g, ' ');

          // Fallback kalkulasi qty_needed jika di database tercatat 0
          let needed = Number(comp.qty_needed) || 0;
          if (needed === 0) {
            if (['botol', 'tutup', 'spray', 'box', 'tambahan'].includes(cat)) {
              needed = prodTargetQty || Number(order.target_qty) || Number(order.final_qty) || 1;
            } else if (['bibit', 'bahan_cair', 'dpg', 'alkohol'].includes(cat) || cat.includes('bahan')) {
              const rMatch = racikanList.find(r => {
                const rNama = (r.nama_bahan || r.bahan_nama || r.nama || '').toLowerCase().trim();
                return rNama && (cleanKey.includes(rNama) || rNama.includes(cleanKey));
              });

              if (rMatch) {
                if (rMatch.kebutuhan_nilai && Number(rMatch.kebutuhan_nilai) > 0) {
                  let val = Number(rMatch.kebutuhan_nilai);
                  const sat = (rMatch.kebutuhan_satuan || rMatch.satuan || '').toLowerCase();
                  const compUnit = (comp.unit || '').toLowerCase();
                  if ((compUnit === 'l' || compUnit === 'liter' || compUnit === 'kg') && (sat === 'ml' || sat === 'g' || sat === 'gr')) {
                    needed = val / 1000;
                  } else if ((compUnit === 'ml' || compUnit === 'g' || compUnit === 'gr') && (sat === 'l' || sat === 'liter' || sat === 'kg')) {
                    needed = val * 1000;
                  } else {
                    needed = val;
                  }
                } else if (rMatch.persentase) {
                  const pct = parseFloat(String(rMatch.persentase).replace(',', '.')) || 0;
                  const mlNeeded = (pct / 100) * totalVolMl;
                  const compUnit = (comp.unit || '').toLowerCase();
                  if (compUnit === 'l' || compUnit === 'liter' || compUnit === 'kg') {
                    needed = mlNeeded / 1000;
                  } else {
                    needed = mlNeeded;
                  }
                }
              }
            }
          }

          // Stock lookup
          const usageKey = invId || cleanKey || 'unknown';
          const priorUsage = runningUsage[usageKey] || 0;

          const rawStock = (invId && stockMap[invId] !== undefined)
            ? stockMap[invId]
            : (cleanKey && stockMap[cleanKey] !== undefined)
              ? stockMap[cleanKey]
              : (comp.stock_available ?? 0);

          const realStock = Math.max(0, rawStock - priorUsage);

          if (needed > 0) {
            runningUsage[usageKey] = priorUsage + needed;
          }

          const arrivedQty = arrivedMap[cleanKey] || 0;
          const isFromClient = comp.vendor === 'Client' || comp.dari_client || ['pending_client', 'ready_client'].includes(comp.status);

          let status = comp.status || 'pending';
          if (comp.status === 'pending_client') {
            status = 'pending_client';
          } else if (comp.status === 'ready_client') {
            status = 'ready_client';
          } else if (needed > 0 && realStock >= needed) {
            status = isFromClient ? 'ready_client' : 'ready';
          } else if (needed > 0 && realStock < needed) {
            status = isFromClient ? 'pending_client' : 'need_buy';
          } else if (realStock > 0) {
            status = isFromClient ? 'ready_client' : 'ready';
          } else {
            status = isFromClient ? 'pending_client' : 'need_buy';
          }

          return {
            ...comp,
            _variantProd: prod,
            qty_needed: Number(needed.toFixed(3)),
            stock_available: realStock,
            status,
            arrived_qty: arrivedQty
          };
        });
      });

      return { 
        ...prod, 
        _originalIndex: originalIndex,
        _uniqueKey: prod._id ? String(prod._id) : `${prod.product_name || prod.variant_name}_${prod.ukuran_botol_ml || prod.botol_ukuran_label || prod.botol_ukuran_aktual || 0}_${originalIndex}`,
        enrichedCore: variantCore 
      };
    });
  }, [order?.products, order?.components, order?.racikan_digunakan, order?.target_qty, order?.final_qty, stockMap, barangMasukList]);

  const variantCounts = useMemo(() => {
    let waiting = 0, in_progress = 0, done = 0;
    enrichedProducts.forEach(p => {
      const st = getVariantStatus(p);
      if (st.category === 'done') done++;
      else if (st.category === 'in_progress') in_progress++;
      else waiting++;
    });
    return { all: enrichedProducts.length, waiting, in_progress, done };
  }, [enrichedProducts, allTasks, order]);

  // Analisis progres agregat multi-varian untuk mengontrol Stepper Global
  const multiVariantProgress = useMemo(() => {
    if (!order?.products || order.products.length <= 1) return null;

    const total = order.products.length;
    let donePackingCount = 0;
    let inPackingCount = 0;
    let doneFillingCount = 0;
    let inFillingCount = 0;
    let waitingCount = 0;
    let shippedCount = 0;

    order.products.forEach(p => {
      const st = getVariantStatus(p);
      if (st.category === 'done' || st.stage === 'done') {
        donePackingCount++;
        if (st.label === 'Sudah Dikirim') shippedCount++;
      } else if (st.stage === 'packing') {
        inPackingCount++;
      } else if (st.stage === 'filling_done') {
        doneFillingCount++;
      } else if (st.stage === 'filling') {
        inFillingCount++;
      } else {
        waitingCount++;
      }
    });

    const allFillingCompleted = (donePackingCount + inPackingCount + doneFillingCount) >= total;
    const anyPackingStarted = (donePackingCount + inPackingCount) > 0;
    const allPackingCompleted = donePackingCount >= total;

    return {
      total,
      donePackingCount,
      inPackingCount,
      doneFillingCount,
      inFillingCount,
      waitingCount,
      shippedCount,
      allFillingCompleted,
      anyPackingStarted,
      allPackingCompleted,
    };
  }, [order?.products, allTasks, order?.status, order?.shipments]);

  const displayedProducts = useMemo(() => {
    let list = enrichedProducts;

    // Filter by tab status
    if (variantStatusFilter !== 'all') {
      list = list.filter(p => {
        const st = getVariantStatus(p);
        return st.category === variantStatusFilter;
      });
    }

    // Filter by search query
    if (variantSearch.trim()) {
      const q = variantSearch.toLowerCase().trim();
      list = list.filter((p, i) => {
        const pName = (p.product_name || p.variant_name || '').toLowerCase();
        const numStr = String(i + 1);
        const sizeStr = String(p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || '');
        const components = (p.enrichedCore || []).flat().map(c => (c.item_name || c.name || '').toLowerCase()).join(' ');
        return pName.includes(q) || numStr === q || sizeStr.includes(q) || components.includes(q);
      });
    }

    return list;
  }, [enrichedProducts, variantSearch, variantStatusFilter, allTasks, order]);

  const allCoreFlat = order?.products?.length > 0 
    ? enrichedProducts.flatMap(p => (p.enrichedCore || []).flat()) 
    : enrichedCore.flat();
  const linkedComps = allCoreFlat.filter(c => c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name || c.name);

  const readyCount = linkedComps.filter(c => c.status === 'ready' || c.status === 'ready_client').length;

  // Hitung progress gabungan: core + addons
  const addonTotal = addons.length;
  const addonReady = addons.filter(a => a.status === 'sudah_diterima').length;
  const totalItems = linkedComps.length + addonTotal;
  const totalReady = readyCount + addonReady;

  const isRunning = !['pending', 'sedang_po_item', 'antrian_produksi'].includes(order?.status);
  const stagePercentages = {
    pending: 0, sedang_po_item: 0, antrian_produksi: 0,
    in_progress: 15, diracik: 25, maserasi: 40,
    menunggu_filling: 50, filling: 60, menunggu_packing: 70,
    packing: 80, siap_kirim: 90, dalam_pengiriman: 95, shipping: 95, done: 100
  };

  let percentage = 0;
  if (isRunning) {
    percentage = stagePercentages[order?.status] || 10;
  } else {
    percentage = totalItems > 0 ? Math.round((totalReady / totalItems) * 100) : 0;
  }

  // Cek apakah shortage items sudah di-PO oleh purchasing
  const { data: purchasingRequests = [] } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list(),
  });

  const isItemPOedInPurchasing = useCallback((comp) => {
    const invId = comp.inventory_item_id;
    const nameKey = (comp.item_name || comp.name || '').toLowerCase().trim();
    const cleanKey = nameKey.replace(/\s+/g, ' ');
    const purchaseStatuses = order?.purchase_statuses || {};

    // 1. Cek purchase_statuses di PO
    if (invId && purchaseStatuses[invId] && !['need_buy', 'pending', 'kurang'].includes(purchaseStatuses[invId])) {
      return true;
    }
    if (cleanKey && purchaseStatuses[cleanKey] && !['need_buy', 'pending', 'kurang'].includes(purchaseStatuses[cleanKey])) {
      return true;
    }

    // 2. Cek PurchasingRequest yang belum deleted
    if (purchasingRequests && purchasingRequests.length > 0) {
      return purchasingRequests.some(req => {
        if (req.data?.deleted_at) return false;
        const reqItem = (req.data?.nama_item || req.nama_item || '').toLowerCase();
        const reqPoId = req.data?.po_id || req.po_id || '';
        if (reqPoId && (reqPoId === order?.id || reqPoId === order?.order_number)) return true;
        if (cleanKey && reqItem.includes(cleanKey)) return true;
        return false;
      });
    }
    return false;
  }, [order?.purchase_statuses, order?.id, order?.order_number, purchasingRequests]);

  // Hitung displayStatus persiapan PO
  const displayStatus = useMemo(() => {
    if (!order) return 'pending';

    // Jika sudah berjalan di lantai produksi (in_progress s/d done)
    const isPastPrep = !['pending', 'sedang_po_item', 'antrian_produksi'].includes(order.status);
    if (isPastPrep) return order.status;

    // Jika 100% bahan & kemasan sudah siap di gudang:
    if (percentage === 100) {
      return 'antrian_produksi';
    }

    // Jika belum 100% -> cek apakah item yang kurang sudah di-PO oleh purchasing
    const needBuyComps = linkedComps.filter(c => c.status === 'need_buy' || c.status === 'pending');
    const isAnyPOed = needBuyComps.length > 0 && needBuyComps.some(c => isItemPOedInPurchasing(c));

    if (isAnyPOed) {
      return 'sedang_po_item';
    }

    return 'pending';
  }, [order?.status, percentage, linkedComps, isItemPOedInPurchasing]);

  React.useEffect(() => {
    // Auto-sync status to database so Marketing, Finance, and other portals stay in sync
    if (order?.id && ['pending', 'sedang_po_item', 'antrian_produksi'].includes(order.status) && displayStatus !== order.status) {
      import('@/api/base44Client').then(({ base44 }) => {
        base44.entities.ProductionOrder.update(order.id, { status: displayStatus })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['production-orders'] });
            queryClient.invalidateQueries({ queryKey: ['production-order', id] });
          })
          .catch(err => console.error('Auto-update PO status failed:', err));
      });
    }
  }, [displayStatus, order?.status, order?.id, id, queryClient]);

  // ─── Production Stage Awareness ───────────────────────────────────────
  // Determine which categories are already consumed based on PO/variant progress
  const STAGES_PAST_RACIK = ['in_progress', 'diracik', 'racik', 'mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];
  const STAGES_PAST_FILLING = ['menunggu_packing', 'packing', 'siap_kirim', 'done', 'selesai'];

  const getVariantRejects = useCallback((vName) => {
    let totalRejects = 0;
    let activities = [];
    allTasks.forEach(task => {
      if (task.member_progress) {
        Object.values(task.member_progress).forEach(prog => {
          if (prog.status === 'completed' && prog.rejected_items) {
            prog.rejected_items.forEach(rj => {
              if (rj.variantName === vName || (!rj.variantName && order?.products?.length === 1)) {
                rj.items.forEach(item => {
                  if (item.qty > 0) {
                    totalRejects += Number(item.qty);
                    if (task.activity_type) activities.push(task.activity_type);
                  }
                });
              }
            });
          }
        });
      }
    });
    return { 
      totalRejects, 
      activities: [...new Set(activities)].map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ') 
    };
  }, [allTasks, order]);

  // Map activity types from tasks to production stages
  const TASK_ACTIVITY_TO_STAGE = {
    racik: 'diracik',
    mixing: 'diracik',
    maserasi: 'maserasi',
    filling: 'filling',
    packing: 'packing',
    packaging: 'packing',
    labeling: 'packing',
    sealing: 'packing',
    qc: 'siap_kirim',
  };

  // Check if a category is already consumed given the current production stage
  const isCategoryConsumed = (category, poStatus) => {
    const normStatus = String(poStatus || order?.status || '').toLowerCase();
    if (['done', 'selesai', 'siap_kirim', 'shipping', 'dalam_pengiriman'].some(s => normStatus.includes(s))) return true;

    const cat = String(category || '').toLowerCase().trim();
    if (['bibit', 'bahan_cair', 'bahan cair', 'bahan', 'pelarut', 'alkohol', 'campuran', 'parfum', 'fragrance', 'dpg', 'peg'].some(c => cat === c || cat.includes(c))) {
      // If PO status or stepper is at or past maserasi / filling / packing -> racik/mixing is already done!
      if (['maserasi', 'filling', 'packing', 'pengemasan', 'siap_kirim', 'done', 'selesai'].some(s => normStatus.includes(s))) return true;

      const mixingTasks = allTasks.filter(t => (t.activity_type === 'racik' || t.activity_type === 'mixing' || (t.task_name && t.task_name.toLowerCase().includes('mixing'))));
      if (mixingTasks.length > 0 && mixingTasks.every(t => t.status === 'completed' || t.status === 'cancelled')) return true;

      const activeTasks = allTasks.filter(t => t.status === 'in_progress' || t.status === 'assigned');
      if (activeTasks.length > 0 && activeTasks.every(t => ['filling', 'packing', 'pengemasan', 'qc'].includes((t.activity_type || '').toLowerCase()))) return true;

      return false;
    }

    if (['botol', 'tutup', 'spray', 'fullset'].some(c => cat === c || cat.includes(c))) {
      // If PO status is at or past packing / pengemasan -> filling is already done!
      if (['packing', 'pengemasan', 'siap_kirim', 'done', 'selesai'].some(s => normStatus.includes(s))) return true;

      const fillingTasks = allTasks.filter(t => t.activity_type === 'filling');
      if (fillingTasks.length > 0 && fillingTasks.every(t => t.status === 'completed' || t.status === 'cancelled')) return true;

      const activeTasks = allTasks.filter(t => t.status === 'in_progress' || t.status === 'assigned');
      if (activeTasks.length > 0 && activeTasks.every(t => ['packing', 'pengemasan', 'qc'].includes((t.activity_type || '').toLowerCase()))) return true;

      return false;
    }

    if (['packaging', 'box', 'kemasan', 'label', 'stiker', 'sticker'].some(c => cat === c || cat.includes(c))) {
      if (['siap_kirim', 'done', 'selesai'].some(s => normStatus.includes(s))) return true;

      const packingTasks = allTasks.filter(t => ['packing', 'pengemasan', 'labeling', 'sealing', 'packaging'].includes(t.activity_type));
      if (packingTasks.length > 0 && packingTasks.every(t => t.status === 'completed' || t.status === 'cancelled')) return true;

      return false;
    }
    return false;
  };

  // For multi-variant: check the "lowest" stage among all variants
  // Only suppress need_buy if ALL variants have passed the relevant stage
  const effectivePOStatus = useMemo(() => {
    // If single product PO, just use order.status
    if (!order?.products?.length) return order?.status || 'pending';

    // For multi-variant: use the earliest (least progressed) variant stage
    const stageOrder = ['pending', 'in_progress', 'diracik', 'racik', 'mixing', 'maserasi', 'menunggu_filling', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done'];

    // Determine each variant's effective stage from tasks
    const variantStages = order.products.map(prod => {
      const vTasks = allTasks.filter(t => t.product_name === prod.product_name || !t.product_name);
      if (vTasks.length === 0) return 'pending';

      // Find the highest completed stage
      let highestStage = 'pending';
      vTasks.forEach(t => {
        const act = (t.activity_type || '').toLowerCase();
        const stage = TASK_ACTIVITY_TO_STAGE[act] || (t.task_name && t.task_name.toLowerCase().includes('mixing') ? 'diracik' : null);
        if (t.status === 'completed' && stage) {
          if (stageOrder.indexOf(stage) > stageOrder.indexOf(highestStage)) {
            highestStage = stage;
          }
        }
        if (t.status === 'in_progress' && stage) {
          if (stageOrder.indexOf(stage) > stageOrder.indexOf(highestStage)) {
            highestStage = stage;
          }
        }
      });
      return highestStage;
    });

    // Use the global PO status if it's further along
    const globalIdx = stageOrder.indexOf(order.status || 'pending');
    // Use the MINIMUM variant stage (most behind) → only suppress if ALL are past
    const minVariantIdx = Math.min(...variantStages.map(s => stageOrder.indexOf(s)));
    const effectiveIdx = (minVariantIdx !== Infinity && minVariantIdx >= 0) ? minVariantIdx : globalIdx;
    return stageOrder[effectiveIdx] || 'pending';
  }, [order, allTasks]);

  // Items yang perlu ditampilkan di NeedBuySection:
  // Hanya tampilkan item yang stoknya KURANG. Otomatis hilang jika stok sudah cukup.
  const allNeedAttentionCompsRaw = linkedComps.filter(c => {
    // If this category is already consumed at the current stage, skip it
    if (isCategoryConsumed(c.category, effectivePOStatus)) return false;
    // Hanya tampilkan jika stok kurang (need_buy / pending_client)
    if (c.status === 'need_buy' || c.status === 'pending_client') return true;
    return false;
  });

  const allNeedAttentionComps = useMemo(() => {
    const map = {};
    allNeedAttentionCompsRaw.forEach(c => {
      const key = c.inventory_item_id || `${c.category}_${c.item_name}`;
      if (!map[key]) {
        map[key] = { ...c };
      } else {
        map[key].qty_needed += (c.qty_needed || 0);
      }
    });
    return Object.values(map);
  }, [allNeedAttentionCompsRaw]);

  const updateMutation = useMutation({
    mutationFn: (data) => base44.entities.ProductionOrder.update(id, data),
    onSuccess: async (_, variables) => {
      // Hard re-fetch data terbaru dari server (bukan dari cache)
      await queryClient.refetchQueries({ queryKey: ['production-order', id] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      if (variables.components !== undefined || variables.products !== undefined) {
        setEditingVariantIdx(null);
      }
    },
    onError: () => {
      toast.error('Gagal menyimpan ke database. Silakan coba lagi.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const poNum = order?.order_number;
      await base44.entities.ProductionOrder.delete(id);
      if (poNum) {
        try {
          const clientPo = await base44.entities.ClientPO.get(poNum);
          if (clientPo) {
            await base44.entities.ClientPO.update(poNum, { statusProduksi: 'BELUM NAIK PRODUKSI' });
          }
        } catch (e) {
          console.error("Failed to reset ClientPO status on deletion:", e);
        }
      }
    },
    onSuccess: () => {
      toast.success('PO Produksi berhasil dihapus!');
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });
      window.location.href = '/office/production/po-list';
    },
    onError: (err) => {
      toast.error('Gagal menghapus PO: ' + (err?.message || 'Error server'));
    }
  });

  const addInventoryItem = useMutation({
    mutationFn: (data) => base44.entities.InventoryItem.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['inventory'] }),
  });

  // Edit Info PO
  const startEditing = () => {
    setEditForm({
      order_number: order.order_number || '',
      brand_name: order.brand_name,
      product_name: order.product_name || '',
      target_qty: order.target_qty || '',
      final_qty: order.final_qty || '',
      deadline: order.deadline || '',
      status: order.status,
      notes: order.notes || '',
      is_dynamic_qty: order.is_dynamic_qty || false,
    });
    setEditing(true);
  };

  const refreshAllInventory = () => {
    queryClient.invalidateQueries({ queryKey: ['production-order', id] });
    queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
    queryClient.invalidateQueries({ queryKey: ['botol'] });
    queryClient.invalidateQueries({ queryKey: ['tutup'] });
    queryClient.invalidateQueries({ queryKey: ['spray'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  const saveEdit = async () => {
    const prevStatus = order.status;
    const newStatus = editForm.status;

    const payload = {
      ...editForm,
      target_qty: Number(editForm.target_qty) || 0,
      final_qty: Number(editForm.final_qty) || 0,
    };

    if (newStatus !== 'done') {
      payload.diselesaikan_langsung = false;
      payload.completion_type = null;
    }

    await updateMutation.mutateAsync(payload);
    setEditing(false);

    // Kurangi stok otomatis saat status berubah ke in_progress (jika belum pernah dikurangi)
    if (prevStatus !== 'in_progress' && newStatus === 'in_progress') {
      if (order.stock_deducted) {
        toast.success('PO berhasil diperbarui. Stok tidak dikurangi lagi (sudah dikurangi sebelumnya).');
        return;
      }
      const orderWithNewStatus = { ...order, ...editForm };
      const { deducted, errors, skipped } = await deductStockForPO(orderWithNewStatus, inventoryItems, bahanList, 'in_progress');
      refreshAllInventory();
      if (skipped) toast.success('PO berhasil diperbarui');
      else if (errors > 0) toast.warning(`Status → In Progress. ${deducted} item stok berkurang, ${errors} item tidak ditemukan di inventori.`);
      else toast.success(`🚀 Status → In Progress! Stok ${deducted} item berhasil dikurangi otomatis.`);
    } else {
      toast.success('PO berhasil diperbarui');
    }
  };

  // Simpan komponen — hanya simpan yang sudah dipilih, tunggu konfirmasi DB
  const saveComponents = async () => {
    const allComponents = [];
    CORE_CATEGORIES.forEach((cat, catIdx) => {
      (coreComponents[catIdx] || []).forEach(comp => {
        if (comp.inventory_item_id || ['pending_client', 'ready_client'].includes(comp.status) || comp.item_name) {
          allComponents.push({
            ...comp,
            name: comp.item_name || cat,
            category: cat,
            is_integrated: true,
            target_qty: comp.qty_needed || 0,
            current_qty: comp.current_qty || 0,
            color_code: comp.color_code || '',
          });
        }
      });
    });
    const payload = {};
    if (editingVariantIdx !== 'global' && order.products?.length > 0) {
      const newProducts = [...order.products];
      newProducts[editingVariantIdx] = {
        ...newProducts[editingVariantIdx],
        ...(variantEditForm || {}),
        target_qty: variantEditForm ? (Number(variantEditForm.target_qty) || 0) : (newProducts[editingVariantIdx].target_qty || 0),
        components: allComponents
      };
      payload.products = newProducts;
    } else {
      payload.components = allComponents;
    }
    await updateMutation.mutateAsync(payload);
    toast.success('Komponen berhasil disimpan ke database');
  };

  const cancelEditCore = () => {
    setEditingVariantIdx(null);
    setCoreComponents(parseCoreComponents(order.components));
  };

  const handleAddonsChange = async (newAddons) => {
    setAddons(newAddons);
    await updateMutation.mutateAsync({ additional_components: newAddons });
    toast.success('Komponen tambahan disimpan');
  };

  // Core comp handlers
  const addCoreComp = (catIdx) => {
    setCoreComponents(prev => {
      const updated = [...prev];
      updated[catIdx] = [...updated[catIdx], makeComp(CORE_CATEGORIES[catIdx])];
      return updated;
    });
  };

  const removeCoreComp = (catIdx, compIdx) => {
    setCoreComponents(prev => {
      const updated = [...prev];
      updated[catIdx] = updated[catIdx].filter((_, i) => i !== compIdx);
      if (updated[catIdx].length === 0) updated[catIdx] = [makeComp(CORE_CATEGORIES[catIdx])];
      return updated;
    });
  };

  const updateCoreComp = (catIdx, compIdx, updatedComp) => {
    setCoreComponents(prev => {
      const updated = [...prev];
      updated[catIdx] = updated[catIdx].map((c, i) => i === compIdx ? updatedComp : c);
      return updated;
    });
  };

  // Atur Pengiriman � Update info ongkir dan ganti status ke shipping (stok belum dikurangi)
  const handleAturPengiriman = async ({ shipments, shipping_via, ongkir_payer, ongkir_amount, pic, resi, shipping_details, final_qty } = {}) => {
    if (!order || markingDone) return;
    setMarkingDone(true);

    const existingShipments = isEditingShipping ? [] : (order.shipments || []);
    const updatedShipments = [...existingShipments, ...(shipments || [])];

    // Calculate total shipped across all shipments
    let totalShipped = 0;
    updatedShipments.forEach(s => {
      (s.items || []).forEach(i => {
        totalShipped += Number(i.qty_akhir) || 0;
      });
    });

    // Calculate total target
    const totalTarget = order.products?.reduce((sum, p) => sum + (Number(p.qty) || Number(p.target_qty) || 0), 0) || Number(order.target_qty) || 0;

    // Calculate total rejects
    let totalRejects = 0;
    allTasks.forEach(task => {
      if (task.po_id === order.id && task.member_progress) {
        Object.values(task.member_progress).forEach(prog => {
          if (prog.status === 'completed' && prog.rejected_items) {
            prog.rejected_items.forEach(rj => {
              rj.items.forEach(item => {
                if (item.qty > 0) totalRejects += Number(item.qty);
              });
            });
          }
        });
      }
    });

    const isFullyShipped = totalShipped >= Math.max(0, totalTarget - totalRejects);
    const isDone = order.status === 'done';
    const nextStatus = isDone ? 'done' : (isFullyShipped ? 'shipping' : order.status);

    // Otomatis kurangi semua sisa stok bahan baku & kemasan jika belum terpotong
    try {
      await deductStockForPO(order, inventoryItems, bahanList, 'shipped');
    } catch (err) {
      console.warn('Gagal memotong stok saat selesai kirim:', err);
    }

    await base44.entities.ProductionOrder.update(id, {
      status: nextStatus,
      is_shipped: true,
      shipped_at: order.shipped_at || new Date().toISOString().split('T')[0],
      shipments: updatedShipments,
      ...(shipping_via && { shipping_via }),
      ...(ongkir_payer && { ongkir_payer }),
      ...(ongkir_amount !== undefined && { ongkir_amount }),
      ...(pic && { pic }),
      ...(resi && { resi }),
      ...(shipping_details && { shipping_details }),
      final_qty: totalShipped,
    });

    // Update invoice status AUTOMATICALLY when Surat Jalan / Pengiriman is set!
    try {
      if (order.order_number) {
        const todayIso = new Date().toISOString();
        const todayDateStr = todayIso.split('T')[0];
        await base44.entities.ClientPO.update(order.order_number, { 
          statusProduksi: isDone ? 'SUDAH SAMPAI' : (isFullyShipped ? 'PESANAN SELESAI' : 'DIKIRIM SEBAGIAN'),
          surat_jalan_date: order.shipped_at || todayDateStr,
          shipped_at: order.shipped_at || todayDateStr,
          tanggal_selesai: isDone ? (order.tanggal_selesai || todayIso) : todayIso
        });
      }
    } catch (e) {
      console.warn('Failed to update invoice status:', e);
    }

    // Invalidate queries to refresh data immediately
    queryClient.invalidateQueries({ queryKey: ['production-order', id] });
    queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
    queryClient.invalidateQueries({ queryKey: ['client-pos'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
    queryClient.invalidateQueries({ queryKey: ['botol'] });
    queryClient.invalidateQueries({ queryKey: ['tutup'] });
    queryClient.invalidateQueries({ queryKey: ['spray'] });
    queryClient.invalidateQueries({ queryKey: ['bahan-cair'] });
    queryClient.invalidateQueries({ queryKey: ['fullset'] });
    if (order.order_number) {
      queryClient.invalidateQueries({ queryKey: ['client-po', order.order_number] });
    }

    setMarkingDone(false);
    setShowSelesaiModal(false);
    setIsEditingShipping(false);

    // Catat log aktivitas pengiriman
    logActivity({
      portal: 'produksi',
      page: 'production_detail',
      action_type: 'shipping_dispatch',
      title: `Atur Pengiriman PO ${order.brand_name || order.order_number}`,
      details: `${isDone ? 'Pembaruan data pengiriman / surat jalan' : 'Pengiriman diatur'} untuk PO #${order.order_number} (${order.brand_name || '-'}), total kirim: ${totalShipped} pcs via ${shipping_via || '-'}, resi: ${resi || '-'}`,
      target_id: String(id),
      target_name: order.brand_name || order.order_number,
    }).catch(console.error);

    toast.success(isDone ? 'Surat Jalan dan data pengiriman berhasil diperbarui.' : (isFullyShipped ? 'Semua pengiriman telah diatur.' : 'Pengiriman sebagian telah diatur.'));
  };

  // Hapus Semua Surat Jalan / Batalkan Seluruh Pengiriman
  const handleHapusAllSuratJalan = async () => {
    if (!order || markingDone) return;
    if (!window.confirm(`Apakah Anda yakin ingin menghapus SEMUA surat jalan & membatalkan pengiriman untuk PO "${order.brand_name || order.order_number}"? Data surat jalan akan dihapus dan tanda pengiriman dibatalkan.`)) {
      return;
    }

    setMarkingDone(true);
    try {
      await base44.entities.ProductionOrder.update(id, {
        is_shipped: false,
        shipments: [],
        shipping_via: null,
        courier: null,
        resi: null,
        shipped_at: null,
        surat_jalan_no: null,
        surat_jalan_date: null,
        diselesaikan_langsung: false,
        completion_type: null,
        status: (order.status === 'shipping' || order.status === 'done') ? 'in_progress' : order.status,
      });

      // Update status ClientPO / Invoice jika terhubung
      if (order.order_number) {
        try {
          await base44.entities.ClientPO.update(order.order_number, {
            statusProduksi: 'PRODUKSI',
            surat_jalan_date: null,
            shipped_at: null,
          });
        } catch (e) {
          console.warn('Failed to revert ClientPO status:', e);
        }
      }

      refreshAllInventory();
      queryClient.invalidateQueries({ queryKey: ['production-order', id] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });

      // Catat log aktivitas pembatalan surat jalan
      logActivity({
        portal: 'produksi',
        page: 'production_detail',
        action_type: 'shipping_cancel',
        title: `Hapus Surat Jalan & Batal Kirim PO ${order.brand_name || order.order_number}`,
        details: `Membatalkan pengiriman dan menghapus seluruh surat jalan untuk PO #${order.order_number} (${order.brand_name || '-'}), status dikembalikan ke ${order.status === 'shipping' || order.status === 'done' ? 'in_progress' : order.status}`,
        target_id: String(id),
        target_name: order.brand_name || order.order_number,
      }).catch(console.error);

      setShowSelesaiModal(false);
      setIsEditingShipping(false);
      setShowHapusSuratJalanModal(false);
      toast.success('✅ Semua Surat Jalan berhasil dihapus dan pengiriman dibatalkan!');
    } catch (err) {
      toast.error('Gagal menghapus surat jalan: ' + err.message);
    } finally {
      setMarkingDone(false);
    }
  };

  // Hapus Salah Satu Destinasi Surat Jalan Saja
  const handleHapusSingleShipment = async (sIdx) => {
    if (!order || markingDone) return;
    const currentShipments = order.shipments || [];
    const targetShipment = currentShipments[sIdx];
    const totalItems = (targetShipment?.items || []).reduce((sum, i) => sum + (Number(i.qty_akhir) || 0), 0);
    const destLabel = `Destinasi ${sIdx + 1}${targetShipment?.penerima ? ` (${targetShipment.penerima})` : ''} - ${totalItems} pcs`;

    if (!window.confirm(`Apakah Anda yakin ingin menghapus Surat Jalan ${destLabel}? Surat jalan destinasi lainnya akan tetap aman.`)) {
      return;
    }

    setMarkingDone(true);
    try {
      const remainingShipments = currentShipments.filter((_, idx) => idx !== sIdx);

      if (remainingShipments.length === 0) {
        // Jika semua destinasi habis, batalkan status pengiriman
        await base44.entities.ProductionOrder.update(id, {
          is_shipped: false,
          shipments: [],
          shipping_via: null,
          courier: null,
          resi: null,
          shipped_at: null,
          surat_jalan_no: null,
          surat_jalan_date: null,
          diselesaikan_langsung: false,
          completion_type: null,
          status: (order.status === 'shipping' || order.status === 'done') ? 'in_progress' : order.status,
        });

        if (order.order_number) {
          try {
            await base44.entities.ClientPO.update(order.order_number, {
              statusProduksi: 'PRODUKSI',
              surat_jalan_date: null,
              shipped_at: null,
            });
          } catch (e) {
            console.warn('Failed to revert ClientPO status:', e);
          }
        }
      } else {
        // Masih ada destinasi lain
        await base44.entities.ProductionOrder.update(id, {
          shipments: remainingShipments,
          shipping_via: remainingShipments[0]?.shipping_via || order.shipping_via,
          courier: remainingShipments[0]?.courier || order.courier,
          resi: remainingShipments[0]?.resi || order.resi,
        });
      }

      refreshAllInventory();
      queryClient.invalidateQueries({ queryKey: ['production-order', id] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });

      logActivity({
        portal: 'produksi',
        page: 'production_detail',
        action_type: 'shipping_cancel',
        title: `Hapus Surat Jalan Destinasi ${sIdx + 1} PO ${order.brand_name || order.order_number}`,
        details: `Menghapus surat jalan destinasi ${sIdx + 1} (${totalItems} pcs). Sisa: ${remainingShipments.length} destinasi.`,
        target_id: String(id),
        target_name: order.brand_name || order.order_number,
      }).catch(console.error);

      setShowHapusSuratJalanModal(false);
      toast.success(`✅ Surat Jalan Destinasi ${sIdx + 1} berhasil dihapus!`);
    } catch (err) {
      toast.error('Gagal menghapus surat jalan: ' + err.message);
    } finally {
      setMarkingDone(false);
    }
  };

  // Handler tombol utama Hapus Surat Jalan
  const handleHapusSuratJalan = () => {
    if (!order || markingDone) return;
    if (order.shipments && order.shipments.length > 1) {
      setShowHapusSuratJalanModal(true);
    } else {
      handleHapusAllSuratJalan();
    }
  };

  // Konfirmasi Barang Sampai — Mengurangi stok dan ganti status ke done
  const executeSelesaiBarangSampai = async () => {
    if (!order || markingDone) return;
    setMarkingDone(true);
    setShowAsaConfirm(false);

    const { deducted, errors, skipped } = await deductStockForPO(order, inventoryItems, bahanList, 'done');

    await base44.entities.ProductionOrder.update(id, {
      status: 'done',
    });

    // Cari invoice terkait lalu ubah statusnya jadi sudah_sampai
    try {
      if (order.order_number) {
        await base44.entities.ClientPO.update(order.order_number, { statusProduksi: 'SUDAH SAMPAI' });
      }
    } catch (e) {
      console.warn('Failed to update invoice status:', e);
    }

    refreshAllInventory();
    queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
    setMarkingDone(false);

    if (skipped) {
      toast.success('PO selesai! (Stok sudah dikurangi sebelumnya)');
    } else if (errors > 0) {
      toast.warning(`PO selesai! ${deducted} item stok dikurangi, ${errors} item tidak ditemukan.`);
    } else {
      toast.success(`✅ PO selesai! Stok ${deducted} item berhasil dikurangi & tercatat di Barang Keluar.`);
    }
  };

  // Selesaikan Langsung (Direct Completion) — Memotong seluruh bahan & kemasan dan menandai selesai dengan modal ASA
  const handleSelesaikanLangsung = () => {
    if (!order || markingDone) return;
    const preview = previewStockDeduction(order, 'all', inventoryItems, bahanList);
    setAsaStockModal({
      open: true,
      title: `Konfirmasi Selesai PO: ${order.brand_name} (#${order.order_number})`,
      taskName: 'Selesaikan PO Langsung',
      poNumber: `PO #${order.order_number}`,
      brandName: order.brand_name,
      activityType: 'direct',
      previewData: preview,
      confirmLabel: 'Konfirmasi & Selesaikan PO',
      onConfirm: async () => {
        await executeDirectCompletion();
        setAsaStockModal(null);
      }
    });
  };

  const executeDirectCompletion = async () => {
    setMarkingDone(true);
    try {
      // 1. Kurangi seluruh bahan baku & packaging yang belum terpotong (tanpa double-deduction)
      await deductStockForPO(order, inventoryItems, bahanList, 'direct');

      const todayIso = new Date().toISOString();
      const todayDateStr = todayIso.split('T')[0];
      const targetQty = order.target_qty || order.final_qty || 0;

      // 2. Update ProductionOrder ke status done dan diselesaikan_langsung
      await base44.entities.ProductionOrder.update(id, {
        status: 'done',
        is_shipped: true,
        diselesaikan_langsung: true,
        completion_type: 'direct',
        shipped_at: order.shipped_at || todayDateStr,
        tanggal_selesai: order.tanggal_selesai || todayIso,
        final_qty: order.final_qty || targetQty,
      });

      // 3. Update status ClientPO / Invoice
      if (order.order_number) {
        try {
          await base44.entities.ClientPO.update(order.order_number, {
            statusProduksi: 'PESANAN SELESAI',
            surat_jalan_date: order.shipped_at || todayDateStr,
            shipped_at: order.shipped_at || todayDateStr,
            tanggal_selesai: order.tanggal_selesai || todayIso,
          });
        } catch (e) {
          console.warn('Failed to update linked invoice:', e);
        }
      }

      // 4. Auto-complete all pending / in-progress tasks for this PO
      try {
        const tasksForPO = await base44.entities.ProductionTask.filter({ po_id: order.id });
        const unfinishedTasks = (tasksForPO || []).filter(t => t.status !== 'completed' && t.status !== 'cancelled');
        for (const task of unfinishedTasks) {
          const targetQty = Number(task.target_qty || task.qty || 0);
          let updatedMemberProgress = task.member_progress;
          if (task.member_progress && typeof task.member_progress === 'object') {
            updatedMemberProgress = { ...task.member_progress };
            Object.keys(updatedMemberProgress).forEach(memberKey => {
              const prog = updatedMemberProgress[memberKey];
              if (prog && prog.status !== 'completed') {
                updatedMemberProgress[memberKey] = {
                  ...prog,
                  status: 'completed',
                  completed_at: prog.completed_at || todayIso,
                  completed_qty: prog.completed_qty || prog.target_qty || targetQty,
                };
              }
            });
          }

          await base44.entities.ProductionTask.update(task.id, {
            status: 'completed',
            end_time: task.end_time || todayIso,
            member_progress: updatedMemberProgress,
            notes: task.notes ? `${task.notes} | Diselesaikan otomatis (Selesai Langsung)` : 'Diselesaikan otomatis (Selesai Langsung)'
          });
        }
      } catch (errTask) {
        console.warn('Failed to auto-complete tasks:', errTask);
      }

      // 5. Invalidate queries
      refreshAllInventory();
      queryClient.invalidateQueries({ queryKey: ['production-order', id] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      queryClient.invalidateQueries({ queryKey: ['shipped-orders'] });
      queryClient.invalidateQueries({ queryKey: ['client-pos'] });
      queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
      queryClient.invalidateQueries({ queryKey: ['production-tasks', id] });
      queryClient.invalidateQueries({ queryKey: ['production-tasks'] });

      toast.success(`✅ PO "${order.brand_name}" diselesaikan! Seluruh tugas produksi otomatis ditandai selesai & stok terpotong.`);
    } catch (err) {
      toast.error('Gagal menyelesaikan PO: ' + err.message);
    } finally {
      setMarkingDone(false);
    }
  };

  const totalTargetDisplay = order?.products?.reduce((sum, p) => sum + (Number(p.qty) || Number(p.target_qty) || 0), 0) || Number(order?.target_qty) || 0;
  let totalRejectsDisplay = 0;
  if (order) {
    allTasks.forEach(task => {
      if (task.po_id === order.id && task.member_progress) {
        Object.values(task.member_progress).forEach(prog => {
          if (prog.status === 'completed' && prog.rejected_items) {
            prog.rejected_items.forEach(rj => {
              rj.items.forEach(item => {
                if (item.qty > 0) totalRejectsDisplay += Number(item.qty);
              });
            });
          }
        });
      }
    });
  }
  const totalShippedDisplay = order?.shipments?.reduce((sum, s) => sum + (s.items || []).reduce((s2, i) => s2 + (Number(i.qty_akhir) || 0), 0), 0) || 0;
  const isFullyShipped = totalShippedDisplay >= Math.max(0, totalTargetDisplay - totalRejectsDisplay);

  if (isLoading || !coreComponents) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
    </div>
  );

  if (!order) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">PO tidak ditemukan</p>
      <Link to="/office/production"><Button variant="outline" className="mt-4">Kembali</Button></Link>
    </div>
  );

  const catColor = { botol: 'bg-blue-50 border-blue-200 text-blue-700', bibit: 'bg-green-50 border-green-200 text-green-700', tutup: 'bg-purple-50 border-purple-200 text-purple-700', spray: 'bg-orange-50 border-orange-200 text-orange-700', bahan_cair: 'bg-cyan-50 border-cyan-200 text-cyan-700', tambahan: 'bg-slate-50 border-slate-200 text-slate-700' };
  const catIcon = { botol: <FlaskConical className="w-4 h-4 text-blue-600" />, bibit: <Leaf className="w-4 h-4 text-green-600" />, tutup: <CircleDot className="w-4 h-4 text-purple-600" />, spray: <Wind className="w-4 h-4 text-orange-600" />, bahan_cair: <Droplets className="w-4 h-4 text-cyan-600" />, tambahan: <Package className="w-4 h-4 text-slate-600" /> };
  const scColor = { ready: 'bg-green-100 text-green-700 border-green-200', ready_client: 'bg-teal-100 text-teal-700 border-teal-200', pending: 'bg-yellow-100 text-yellow-700 border-yellow-200', pending_client: 'bg-sky-100 text-sky-700 border-sky-200', need_buy: 'bg-red-100 text-red-700 border-red-200' };

  return (
    <div className="space-y-6 max-w-4xl mx-auto pt-1">
      {/* Back + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-1 print:hidden">
        <Link to="/office/production/po-list" className="shrink-0">
          <Button variant="ghost" size="sm" className="gap-1.5 md:gap-2 text-xs md:text-sm px-2.5 md:px-3 h-8 md:h-9 text-slate-600 bg-white border border-[#E6D5B8]/80 hover:bg-[#F9F5EC] shadow-2xs font-semibold">
            <ArrowLeft className="w-3.5 h-3.5" /> Kembali
          </Button>
        </Link>
        <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
          {canEdit && !editing && (
            <Button variant="outline" size="sm" className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2 md:px-3 h-8 md:h-9 bg-white border-[#E6D5B8]/60 hover:bg-[#F9F5EC]" onClick={startEditing}>
              <Edit2 className="w-3 h-3 text-slate-500" /> <span className="font-semibold text-slate-700">Edit Info</span>
            </Button>
          )}
          {canEdit && editing && (
            <>
              <Button size="sm" className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2 md:px-3 h-8 md:h-9" onClick={saveEdit} disabled={updateMutation.isPending}>
                <Save className="w-3 h-3" /> Simpan Info
              </Button>
              <Button variant="ghost" size="sm" className="text-[10px] md:text-sm h-8 md:h-9" onClick={() => setEditing(false)}>Batal</Button>
            </>
          )}

          {canEdit && (
            <Button size="sm" variant="outline" className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2.5 md:px-3.5 h-8 md:h-9 bg-blue-50 hover:bg-blue-100 text-blue-900 border-blue-300 font-bold shadow-2xs"
              onClick={() => setShowSpkModal(true)}
              title="Cek dan Cetak Surat Perintah Kerja (SPK) untuk PO ini"
            >
              <FileText className="w-3.5 h-3.5 text-blue-700" />
              Cetak SPK
            </Button>
          )}

          {!editing && (
            <Button size="sm" className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2 md:px-3 h-8 md:h-9 bg-blue-600 hover:bg-blue-700 shadow-sm font-bold"
              onClick={() => {
                setIsEditingShipping(order.is_shipped || order.status === 'done' || (order.shipments && order.shipments.length > 0));
                setShowSelesaiModal(true);
              }}
              disabled={markingDone}
              title={order.status === 'done' || order.is_shipped ? "Revisi / Edit Pengiriman & Surat Jalan" : "Atur Pengiriman"}
            >
              <Truck className="w-3.5 h-3.5" />
              {markingDone ? 'Memproses...' : (order.status === 'done' || isFullyShipped) ? 'Atur / Revisi Surat Jalan' : order.is_shipped ? '+ Tambah Pengiriman' : 'Atur Pengiriman'}
            </Button>
          )}

          {!editing && canEdit && order.status !== 'done' && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2.5 md:px-3 h-8 md:h-9 bg-amber-50 hover:bg-amber-100 text-amber-900 border-amber-300 shadow-sm font-bold"
              onClick={handleSelesaikanLangsung}
              disabled={markingDone}
              title="Selesaikan PO ini secara langsung (Stok bahan baku & packaging akan otomatis terpotong)"
            >
              <Zap className="w-3.5 h-3.5 text-amber-700" />
              {markingDone ? 'Memproses...' : 'Selesai Langsung'}
            </Button>
          )}

          {!editing && order.is_shipped && (
            <>
              {/* Surat Jalan button moved to cards below */}
              {canEdit && order.status === 'shipping' && isFullyShipped && (
                <Button size="sm" className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2 md:px-3 h-8 md:h-9 bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-600"
                  onClick={() => setShowAsaConfirm(true)}
                  disabled={markingDone}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {markingDone ? 'Memproses...' : 'Selesai (Barang Sampai)'}
                </Button>
              )}
            </>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1.5 md:gap-2 text-[10px] md:text-sm px-2.5 md:px-3 h-8 md:h-9 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 bg-white shadow-sm font-bold"
            onClick={() => { 
              if (window.confirm(`Apakah Anda yakin ingin menghapus PO "${order?.brand_name || order?.product_name || 'ini'}"?`)) {
                deleteMutation.mutate(); 
              }
            }}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
            {deleteMutation.isPending ? 'Menghapus...' : 'Hapus PO'}
          </Button>
        </div>
      </div>

      {/* Shipped banner */}
      {order.is_shipped && order.status === 'shipping' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg md:rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <Truck className="w-4 h-4 md:w-5 md:h-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs md:text-sm text-blue-800">PO Dalam Pengiriman</p>
            <p className="text-[10px] md:text-xs text-blue-700 mt-0.5 break-words">PO telah dikirim via {order.shipping_via || '-'}, menunggu konfirmasi sampai.</p>
          </div>
        </div>
      )}
      {order.is_shipped && order.status === 'done' && (
        <div className="bg-green-50 border border-green-200 rounded-lg md:rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs md:text-sm text-green-800">PO Selesai & Telah Sampai</p>
            <p className="text-[10px] md:text-xs text-green-700 mt-0.5 break-words">
              Terkirim pada {order.shipped_at ? new Date(order.shipped_at).toLocaleDateString('id-ID') : '-'} via {order.shipping_via || '-'}. Stok telah dikurangi secara otomatis.
            </p>
          </div>
        </div>
      )}

      {/* Returned banner */}
      {order.is_shipped && order.status === 'returned' && (
        <div className="bg-red-50 border border-red-200 rounded-lg md:rounded-xl p-3 md:p-4 flex items-center gap-2 md:gap-3">
          <RotateCcw className="w-4 h-4 md:w-5 md:h-5 text-red-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-xs md:text-sm text-red-800">PO Dikembalikan (Retur)</p>
            <p className="text-[10px] md:text-xs text-red-700 mt-0.5 break-words">
              PO ini ditandai sebagai dikembalikan.
            </p>
          </div>
        </div>
      )}

      {/* Need buy section dengan tracking status pembelian */}
      {allNeedAttentionComps.length > 0 && !order.is_shipped && (
        <NeedBuySection
          needBuyComps={allNeedAttentionComps}
          onMarkArrived={async (comp) => {
            const newStatus = 'ready_client';
            const newComps = [...(order.components || [])];
            const matchIdx = newComps.findIndex(c => 
              (c.item_name === comp.item_name || c.name === comp.item_name) && 
              (c.category === comp.category || !comp.category)
            );
            if (matchIdx >= 0) {
              newComps[matchIdx] = { ...newComps[matchIdx], status: newStatus, stock_override: comp.qty_needed || newComps[matchIdx].qty };
            } else {
              newComps.push({
                category: comp.category || 'Packaging',
                item_name: comp.item_name,
                status: newStatus,
                qty: comp.qty_needed,
                stock_override: comp.qty_needed,
              });
            }
            const payload = { components: newComps };
            if (order.products?.length > 0) {
              payload.products = order.products.map(p => ({
                ...p,
                components: (p.components || []).map(pc => 
                  (pc.item_name === comp.item_name || pc.name === comp.item_name) 
                    ? { ...pc, status: newStatus, stock_override: comp.qty_needed } 
                    : pc
                )
              }));
            }
            await updateMutation.mutateAsync(payload);
            toast.success(`✅ ${comp.item_name} berhasil ditandai sudah datang / diterima!`);
          }}
        />
      )}

      {/* Daftar Surat Jalan (Multi-Destinasi / Parsial) */}
      {!editing && (order.is_shipped || order.status === 'done' || (order.shipments && order.shipments.length > 0)) && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-sm">Informasi Surat Jalan</h3>
            <div className="flex items-center gap-2 print:hidden">
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8 text-primary border-primary bg-primary/5 hover:bg-primary/10 gap-1.5"
                onClick={() => {
                  setIsEditingShipping(true);
                  setShowSelesaiModal(true);
                }}
              >
                <FileText className="w-3.5 h-3.5" /> Edit Pengiriman
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-8 text-rose-600 border-rose-300 bg-rose-50 hover:bg-rose-100 hover:text-rose-700 gap-1.5 font-bold"
                onClick={handleHapusSuratJalan}
                disabled={markingDone}
                title="Hapus surat jalan dan batalkan pengiriman"
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-600" /> Hapus Surat Jalan
              </Button>
            </div>
          </div>
          {(order.shipments && order.shipments.length > 0) ? (
            order.shipments.map((shipment, sIdx) => {
              const totalItems = (shipment.items || []).reduce((sum, i) => sum + (Number(i.qty_akhir) || 0), 0);
              return (
                <div key={shipment.id || sIdx} className="bg-white border border-[#E6D5B8]/60 rounded-xl p-3 md:p-4 flex flex-col md:flex-row gap-3 md:items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-sm">Surat Jalan Destinasi {sIdx + 1}</h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Via {shipment.shipping_via || '-'} • {shipment.resi ? `Resi: ${shipment.resi}` : 'Resi belum diinput'} • {totalItems} pcs
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2 bg-[#F9F5EC] border-[#E6D5B8] hover:bg-[#F2E8D5] text-[#8C6208] font-bold print:hidden shadow-2xs"
                      onClick={() => setAsaPrintTarget({
                        order,
                        shipment: { ...shipment, pic: shipment.pic || order?.pic, _index: sIdx },
                        isLegacy: false
                      })}
                    >
                      <Printer className="w-4 h-4 text-[#8C6208]" /> Cetak Dokumen Pengiriman
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 text-rose-600 border-rose-200 bg-rose-50/60 hover:bg-rose-100 hover:text-rose-700 font-bold gap-1 print:hidden shadow-2xs"
                      onClick={() => handleHapusSingleShipment(sIdx)}
                      disabled={markingDone}
                      title={`Hapus Surat Jalan Destinasi ${sIdx + 1}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      <span className="hidden sm:inline">Hapus</span>
                    </Button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="bg-white border border-[#E6D5B8]/60 rounded-xl p-3 md:p-4 flex flex-col md:flex-row gap-3 md:items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Surat Jalan (Utama)</h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Via {order.shipping_via || order.courier || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2 bg-[#F9F5EC] border-[#E6D5B8] hover:bg-[#F2E8D5] text-[#8C6208] font-bold print:hidden shadow-2xs"
                  onClick={() => setAsaPrintTarget({
                    order,
                    shipment: { pic: order?.pic },
                    isLegacy: true
                  })}
                >
                  <Printer className="w-4 h-4 text-[#8C6208]" /> Cetak Dokumen Pengiriman
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header Card */}
      <Card className="border-[#E6D5B8]/60 shadow-sm bg-white">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col gap-4 md:gap-8">
            <div className="flex-1">
              {editing ? (
                <div className="space-y-3">
                  <div><Label className="text-xs">Nomor Invoice (PO)</Label><Input value={editForm.order_number} onChange={(e) => setEditForm(f => ({...f, order_number: e.target.value}))} className="font-mono mt-1" /></div>
                  <div><Label className="text-xs">Nama Brand</Label><Input value={editForm.brand_name} onChange={(e) => setEditForm(f => ({...f, brand_name: e.target.value}))} className="font-display font-bold mt-1" /></div>
                  <div><Label className="text-xs">Nama Produk</Label><Input value={editForm.product_name} onChange={(e) => setEditForm(f => ({...f, product_name: e.target.value}))} placeholder="Nama produk" className="mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Target Qty</Label><Input type="number" value={editForm.target_qty} onChange={(e) => setEditForm(f => ({...f, target_qty: e.target.value}))} className="mt-1" /></div>
                    <div><Label className="text-xs">Final Qty</Label><Input type="number" value={editForm.final_qty} onChange={(e) => setEditForm(f => ({...f, final_qty: e.target.value}))} className="mt-1" /></div>
                  </div>
                  <div><Label className="text-xs">Deadline</Label><CustomDatePicker value={editForm.deadline} onChange={(val) => setEditForm(f => ({...f, deadline: val}))} className="mt-1" /></div>
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pending', 'in_progress', 'diracik', 'maserasi', 'filling', 'menunggu_packing', 'packing', 'siap_kirim', 'done'].map(s => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase().replace('PACKING', 'PENGEMASAN')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={editForm.is_dynamic_qty} onCheckedChange={v => setEditForm(f => ({ ...f, is_dynamic_qty: v }))} />
                    <Label className="font-normal text-sm">Dynamic Qty</Label>
                  </div>
                  <div><Label className="text-xs">Catatan</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm(f => ({...f, notes: e.target.value}))} placeholder="Catatan..." className="mt-1 resize-none" rows={2} /></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-xs font-mono font-medium text-slate-500">#{order.order_number}</span>
                    <StatusBadge status={displayStatus} />
                    {isDirectCompletion && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300 shadow-xs">
                        <Zap className="w-3 h-3 text-amber-700" /> Diselesaikan Langsung
                      </span>
                    )}
                    {order.is_dynamic_qty && <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><Lock className="w-3 h-3" /> Dynamic Qty</span>}
                  </div>
                  <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-800">{order.brand_name ? order.brand_name.split('/')[0].trim() : ''}</h1>
                  {order.product_name ? (
                    <p className="text-xs md:text-sm text-slate-500 mb-3">
                      {order.product_name}
                      {(() => {
                        const dbBahan = databaseBahanList.find(b => 
                          (b.nama_varian || '').trim().toLowerCase() === (order.product_name || '').trim().toLowerCase() && 
                          (b.nama_brand || '').trim().toLowerCase() === (order.brand_name || '').split('/')[0].trim().toLowerCase()
                        );
                        const actual = order.botol_ukuran_aktual || 0;
                        const label = order.botol_ukuran_label || order.botol_ukuran_client || dbBahan?.botol_ukuran_label_ml || 0;
                        const sizeStr = label ? `${label}ml` : '';
                        const actualStr = actual > 0 && String(actual) !== String(label) ? ` (Isi ${actual}ml)` : '';
                        return sizeStr ? ` (${sizeStr}${actualStr})` : '';
                      })()}
                    </p>
                  ) : order.products?.length > 1 ? (
                    <p className="text-xs md:text-sm text-slate-500 mb-3">Multi ({order.products.length} Produk)</p>
                  ) : null}
                  
                  {/* Action Buttons */}
                  {!editing && ['siap_kirim', 'shipping', 'dalam_pengiriman', 'packing', 'menunggu_packing', 'filling', 'maserasi', 'diracik', 'in_progress', 'pending'].includes(order.status) && (
                    <div className="flex flex-wrap items-center gap-2">
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] md:text-sm text-slate-500">
                    {order.target_qty > 0 && <span>Target: <strong className="text-slate-800">{order.target_qty?.toLocaleString()} pcs</strong></span>}
                    {order.target_qty > 0 && order.final_qty > 0 && <span className="text-slate-300">|</span>}
                    {order.final_qty > 0 && <span>Final: <strong className="text-slate-800">{order.final_qty?.toLocaleString()} pcs</strong></span>}
                    {(order.target_qty > 0 || order.final_qty > 0) && order.deadline && <span className="text-slate-300">|</span>}
                    {order.deadline && <span>Deadline: <strong className="text-slate-800">{new Date(order.deadline).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}</strong></span>}
                    {(order.target_qty > 0 || order.final_qty > 0 || order.deadline) && order.pic_marketing && <span className="text-slate-300">|</span>}
                    {order.pic_marketing && <span className="flex items-center gap-1">PIC: <strong className="text-slate-800 flex items-center gap-1"><UserCircle2 className="w-3 h-3 text-[#8C6208]" /> {order.pic_marketing}</strong></span>}
                    {order.created_date && <span className="text-slate-300">|</span>}
                    {order.created_date && <span>Naik Produksi: <strong className="text-slate-800">{new Date(order.created_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></span>}
                  </div>
                  
                  {order.notes && <p className="mt-3 text-[10px] md:text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 px-3 break-words whitespace-pre-wrap">{order.notes.split(' | Est.')[0]}</p>}

                </>
              )}
            </div>

            {/* Dynamic Progress Indicator */}
            {!editing && (() => {
              const isRunning = !['pending', 'sedang_po_item', 'antrian_produksi'].includes(order?.status);

              if (isRunning) {
                const steps = [
                  { key: 'racik', label: 'Racik', triggers: ['in_progress', 'diracik', 'menunggu_qc_mixing'] },
                  { key: 'maserasi', label: 'Maserasi', triggers: ['maserasi'] },
                  { key: 'filling', label: 'Filling', triggers: ['filling', 'menunggu_qc_filling'] },
                  { key: 'packing', label: 'Pengemasan', triggers: ['packing', 'waiting_filling', 'menunggu_qc_packing'] },
                  { key: 'siap_kirim', label: 'Siap Kirim', triggers: ['siap_kirim'] },
                  { key: 'pengiriman', label: 'Pengiriman', triggers: ['dalam_pengiriman', 'shipping'] },
                  { key: 'selesai', label: 'Selesai', triggers: ['done', 'is_shipped'] },
                ];

                let currentIndex = 0;
                if (order?.status === 'menunggu_qc_mixing') {
                  currentIndex = 0.5;
                } else if (order?.status === 'menunggu_filling') {
                  currentIndex = 1.5;
                } else if (order?.status === 'menunggu_qc_filling') {
                  currentIndex = 2.4;
                } else if (order?.status === 'menunggu_packing') {
                  currentIndex = 2.6;
                } else if (order?.status === 'menunggu_qc_packing' || order?.status === 'waiting_filling') {
                  currentIndex = 3.5;
                } else {
                  for (let i = 0; i < steps.length; i++) {
                    if (steps[i].triggers.includes(order?.status)) {
                      currentIndex = i;
                      break;
                    }
                  }
                }

                // Jika PO multi-varian dan belum semua varian selesai filling:
                // Tahan stepper di antara Filling dan Pengemasan (2.5) jika status PO sudah packing/menunggu_packing
                const isMulti = multiVariantProgress && multiVariantProgress.total > 1;
                if (isMulti && !multiVariantProgress.allFillingCompleted) {
                  if (currentIndex >= 3 && !['siap_kirim', 'dalam_pengiriman', 'shipping', 'done'].includes(order?.status)) {
                    currentIndex = 2.5;
                  }
                }

                const totalSteps = steps.length - 1; // 6 intervals

                return (
                  <div className="w-full max-w-4xl mx-auto mt-4 md:mt-6 mb-6 md:mb-8">
                    <div className="relative flex items-center justify-between w-full px-2 md:px-5">
                      {/* Background Line */}
                      <div className="absolute top-1/2 left-3 md:left-6 right-3 md:right-6 h-[1.5px] md:h-[3px] bg-slate-100 -translate-y-1/2 z-0 rounded-full" />

                      {/* Active Line */}
                      <div
                        className="absolute top-1/2 left-3 md:left-6 h-[1.5px] md:h-[3px] bg-gradient-to-r from-[#D4AF37] to-[#8C6208] -translate-y-1/2 z-0 transition-all duration-700 ease-in-out rounded-full"
                        style={{ width: `calc(${(currentIndex / totalSteps) * 100}% - ${currentIndex === 0 ? 0 : 16}px)` }}
                      />

                      {/* Floating Intermediary Node: Menunggu QC - Mixing */}
                      {order?.status === 'menunggu_qc_mixing' && (
                        <div className="absolute top-1/2 left-[8.33%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center bg-white border-[3px] border-purple-600 shadow-[0_0_8px_rgba(147,51,234,0.3)]">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-purple-600 animate-pulse" />
                          </div>
                          <div className="absolute -top-7 md:-top-8 text-[9px] md:text-[10px] font-bold text-purple-800 tracking-wide whitespace-nowrap bg-purple-50 px-2.5 py-0.5 md:py-1 rounded-full border border-purple-200 shadow-xs">
                            Menunggu QC Mixing
                          </div>
                        </div>
                      )}

                      {/* Floating Intermediary Node: Menunggu Filling */}
                      {order?.status === 'menunggu_filling' && (
                        <div className="absolute top-1/2 left-[25%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center bg-white border-[3px] border-[#8C6208] shadow-[0_0_8px_rgba(140,98,8,0.2)]">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8C6208] animate-pulse" />
                          </div>
                          <div className="absolute -top-7 md:-top-8 text-[9px] md:text-[10px] font-bold text-amber-700 tracking-wide whitespace-nowrap bg-[#FFFDF3] px-2.5 py-0.5 md:py-1 rounded-full border border-[#F6E9CC] shadow-xs">
                            Menunggu Filling
                          </div>
                        </div>
                      )}

                      {/* Floating Intermediary Node: Menunggu QC - Filling */}
                      {order?.status === 'menunggu_qc_filling' && (
                        <div className="absolute top-1/2 left-[40%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center bg-white border-[3px] border-indigo-600 shadow-[0_0_8px_rgba(79,70,229,0.3)]">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-indigo-600 animate-pulse" />
                          </div>
                          <div className="absolute -top-7 md:-top-8 text-[9px] md:text-[10px] font-bold text-indigo-800 tracking-wide whitespace-nowrap bg-indigo-50 px-2.5 py-0.5 md:py-1 rounded-full border border-indigo-200 shadow-xs">
                            Menunggu QC Filling
                          </div>
                        </div>
                      )}

                      {/* Floating Intermediary Node: Menunggu Packing */}
                      {order?.status === 'menunggu_packing' && (
                        <div className="absolute top-1/2 left-[43.5%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center bg-white border-[3px] border-[#8C6208] shadow-[0_0_8px_rgba(140,98,8,0.2)]">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8C6208] animate-pulse" />
                          </div>
                          <div className="absolute -top-7 md:-top-8 text-[9px] md:text-[10px] font-bold text-amber-700 tracking-wide whitespace-nowrap bg-[#FFFDF3] px-2.5 py-0.5 md:py-1 rounded-full border border-[#F6E9CC] shadow-xs">
                            Menunggu Pengemasan
                          </div>
                        </div>
                      )}

                      {/* Floating Intermediary Node: Menunggu QC - Pengemasan */}
                      {(order?.status === 'menunggu_qc_packing' || order?.status === 'waiting_filling') && (
                        <div className="absolute top-1/2 left-[58.33%] -translate-x-1/2 -translate-y-1/2 z-20 flex flex-col items-center">
                          <div className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center bg-white border-[3px] border-purple-600 shadow-[0_0_8px_rgba(147,51,234,0.3)]">
                            <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-purple-600 animate-pulse" />
                          </div>
                          <div className="absolute -top-7 md:-top-8 text-[9px] md:text-[10px] font-bold text-purple-800 tracking-wide whitespace-nowrap bg-purple-50 px-2.5 py-0.5 md:py-1 rounded-full border border-purple-200 shadow-xs">
                            {order?.status === 'waiting_filling' ? 'Pengemasan Selesai (Menunggu Filling)' : 'Menunggu QC Pengemasan'}
                          </div>
                        </div>
                      )}

                      {/* Nodes */}
                      {steps.map((step, idx) => {
                        const isPast = idx < currentIndex;
                        const isActive = Math.floor(currentIndex) === idx || idx === currentIndex;

                        return (
                          <div key={step.key} className="relative z-10 flex flex-col items-center">
                            <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-500 shadow-xs
                              ${isPast ? 'bg-[#8C6208] text-white border-none' :
                                isActive ? 'bg-white border-[3px] md:border-[4px] border-[#8C6208] shadow-[0_0_12px_rgba(140,98,8,0.3)]' :
                                  'bg-white border-2 md:border-[3px] border-slate-200 text-slate-300'}`
                            }>
                              {isPast && (
                                <svg className="w-3 h-3 md:w-4 md:h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {isActive && (
                                step.key === 'pengiriman' ? (
                                  <Truck className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] animate-pulse" />
                                ) : step.key === 'siap_kirim' ? (
                                  <PackageCheck className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208]" />
                                ) : (
                                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#8C6208]" />
                                )
                              )}
                            </div>
                            <span className={`absolute top-7 md:top-10 text-[8px] md:text-[11px] font-bold tracking-tight md:tracking-wide transition-colors duration-300 whitespace-nowrap
                              ${isActive ? 'text-[#8C6208]' : isPast ? 'text-slate-700' : 'text-slate-400'}`
                            }>
                              {step.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Status Info Banner untuk Multi-Varian Alur Simultan / Sebagian */}
                    {isMulti && multiVariantProgress.anyPackingStarted && !multiVariantProgress.allFillingCompleted && (
                      <div className="flex items-center gap-3 bg-amber-50/90 border border-amber-200/90 rounded-2xl p-3 px-4 mt-6 max-w-2xl mx-auto shadow-2xs">
                        <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center text-amber-800 shrink-0">
                          <Layers className="w-4 h-4 text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs font-bold text-amber-900">Alur Simultan: Sebagian Varian Sudah Masuk Meja Pengemasan</p>
                            <span className="text-[10px] font-extrabold bg-amber-200/80 text-amber-900 px-2 py-0.5 rounded-full border border-amber-300">
                              {multiVariantProgress.donePackingCount + multiVariantProgress.inPackingCount} dari {multiVariantProgress.total} Varian Dikemas
                            </span>
                          </div>
                          <p className="text-[11px] text-amber-800 mt-0.5">
                            Varian yang telah selesai di-filling langsung diproses di meja pengemasan, sementara {multiVariantProgress.total - (multiVariantProgress.donePackingCount + multiVariantProgress.inPackingCount)} varian lainnya masih berjalan di tahap filling atau sebelumnya.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Status Info Banner untuk Siap Kirim & Pengiriman */}
                    {order?.status === 'siap_kirim' && (
                      <div className="flex items-center gap-3 bg-teal-50/80 border border-teal-200/80 rounded-2xl p-3 px-4 mt-6 max-w-xl mx-auto shadow-2xs">
                        <div className="w-8 h-8 rounded-xl bg-teal-100 flex items-center justify-center text-teal-700 shrink-0">
                          <PackageCheck className="w-4 h-4 text-teal-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-teal-900">Produk Selesai Dikemas & Siap Dikirim</p>
                          <p className="text-[11px] text-teal-700 mt-0.5">
                            Barang berada di holding area gudang barang jadi menunggu surat jalan / kurir ekspedisi.
                          </p>
                        </div>
                      </div>
                    )}

                    {['dalam_pengiriman', 'shipping'].includes(order?.status) && (
                      <div className="flex items-center gap-3 bg-blue-50/80 border border-blue-200/80 rounded-2xl p-3 px-4 mt-6 max-w-xl mx-auto shadow-2xs">
                        <div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 shrink-0">
                          <Truck className="w-4 h-4 text-blue-700 animate-pulse" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold text-blue-900">Pesanan Sedang Dalam Perjalanan Pengiriman</p>
                          <p className="text-[11px] text-blue-700 mt-0.5 truncate">
                            Via {order.shipping_via || order.courier || 'Ekspedisi'} {order.resi ? `• No. Resi: ${order.resi}` : ''}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Pending
              return (
                <div className="flex items-center gap-5 bg-slate-50/50 p-5 rounded-2xl border border-slate-100/60 max-w-lg mx-auto mt-4">
                  <ProgressRing percentage={percentage} size={56} strokeWidth={5} isStageMode={false} />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-bold text-slate-700">Persiapan Item & Bahan</p>
                    <p className="text-sm text-slate-500 font-medium mt-0.5">Sedang melengkapi stok untuk kebutuhan produksi</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* ===== KOMPONEN UTAMA (Tampil global hanya jika tidak ada variant) ===== */}
      {!order.products?.length && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-display text-lg">Komponen Utama</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Botol, Bibit, Tutup, Spray — terintegrasi dengan inventori.</p>
              </div>
              {canEdit && editingVariantIdx !== 'global' && (
                <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8" onClick={() => {
                  setCoreComponents(parseCoreComponents(order.components));
                  setEditingVariantIdx('global');
                }}>
                  <Edit2 className="w-3 h-3" /> Edit Komponen
                </Button>
              )}
              {editingVariantIdx === 'global' && (
                <div className="flex gap-2">
                  <Button size="sm" className="gap-1.5 text-xs h-8 bg-green-600 hover:bg-green-700" onClick={saveComponents} disabled={updateMutation.isPending}>
                    <Save className="w-3 h-3" /> Simpan
                  </Button>
                  <Button variant="ghost" size="sm" className="text-xs h-8" onClick={cancelEditCore}>
                    Batal
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* MODE VIEW: locked, tampilkan summary */}
            {editingVariantIdx !== 'global' && (
              <div className="space-y-2">
                {CORE_CATEGORIES.map((cat, catIdx) => {
                  const comps = enrichedCore[catIdx].filter(c => c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name);
                  const catLabel = cat.charAt(0).toUpperCase() + cat.slice(1);
                  if (comps.length === 0) {
                    return null; // Do not show empty categories for view mode
                  }
                  return (
                    <div key={cat} className="space-y-1.5">
                      {comps.map((comp, i) => {
                        const isFromClient = ['pending_client', 'ready_client'].includes(comp.status);
                        return (
                        <div key={i} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${isFromClient ? 'bg-sky-50 border-sky-200' : catColor[cat]}`}>
                          <div className="w-7 h-7 bg-white rounded-md shadow-sm border flex items-center justify-center shrink-0">
                            {isFromClient ? <User className="w-4 h-4 text-sky-600" /> : catIcon[cat]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-semibold truncate">{comp.item_name || catLabel}</p>
                              {isFromClient && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-semibold shrink-0">
                                  <User className="w-3 h-3" /> Dari Client
                                </span>
                              )}
                            </div>
                            {comp.vendor && <p className="text-xs opacity-70 truncate">{comp.vendor}</p>}
                          </div>
                          {!isFromClient && (
                            <div className="text-right shrink-0">
                              {(() => {
                                const { display: qDisp, unit: qUnit } = smartDisplayQty(comp.qty_needed || 0, comp.unit || 'pcs');
                                return <p className="text-sm font-medium">{qDisp} {qUnit}</p>;
                              })()}
                              {(() => {
                                const { display, unit: du } = smartDisplayQty(comp.stock_available || 0, comp.unit || 'pcs');
                                return <p className="text-xs opacity-70">Stok: {display} {du}</p>;
                              })()}
                            </div>
                          )}
                          {isFromClient ? (
                            <button
                              onClick={async () => {
                                const newStatus = comp.status === 'ready_client' ? 'pending_client' : 'ready_client';
                                const newComps = [...(order.components || [])];
                                const matchIdx = newComps.findIndex(c => c.category === cat && c.item_name === comp.item_name && ['pending_client', 'ready_client'].includes(c.status));
                                if (matchIdx >= 0) {
                                  newComps[matchIdx] = { ...newComps[matchIdx], status: newStatus };
                                  const payload = { components: newComps };
                                  if (order.products?.length > 0) {
                                    payload.products = order.products.map(p => ({ ...p, components: newComps }));
                                  }
                                  await updateMutation.mutateAsync(payload);
                                  toast.success(newStatus === 'ready_client' ? '✅ Item dari client ditandai sudah sampai!' : 'Item dikembalikan ke menunggu');
                                }
                              }}
                              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold transition-all shrink-0 cursor-pointer
                                ${comp.status === 'ready_client'
                                  ? 'bg-teal-100 text-teal-700 border-teal-300 hover:bg-teal-200'
                                  : 'bg-white text-sky-700 border-sky-300 hover:bg-sky-50 hover:border-sky-400 shadow-sm'}`}
                            >
                              {comp.status === 'ready_client'
                                ? <><CheckCircle2 className="w-3.5 h-3.5" /> Sudah Sampai</>
                                : <><Package className="w-3.5 h-3.5" /> Tandai Sampai</>}
                            </button>
                          ) : (
                            <>
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${scColor[comp.status] || scColor.pending}`}>
                                {comp.status === 'ready' ? 'Ready' : comp.status === 'need_buy' ? 'Habis di Inventori' : 'Pending'}
                              </span>
                              <Lock className="w-3 h-3 opacity-40 shrink-0" title="Terkunci" />
                            </>
                          )}
                        </div>
                      );})}
                    </div>
                  );
                })}
              </div>
            )}

            {/* MODE EDIT: pilih item inventori */}
            {editingVariantIdx === 'global' && (
              <div className="space-y-4">
                {CORE_CATEGORIES.map((cat, catIdx) => {
                  const isHidden = !['botol', 'tutup', 'spray', 'bibit'].includes(cat) && coreComponents[catIdx].length === 0;
                  if (isHidden) return null; // Can still add via a main button if needed, or we just render them
                  return (
                    <div key={cat} className="space-y-2">
                      {coreComponents[catIdx].map((comp, compIdx) => (
                        <POComponentItem
                          key={`${cat}-${compIdx}`}
                          comp={comp}
                          inventoryItems={inventoryItems}
                          onChange={(updated) => updateCoreComp(catIdx, compIdx, updated)}
                          onRemove={() => removeCoreComp(catIdx, compIdx)}
                          isCore={['botol', 'tutup', 'spray'].includes(cat) && coreComponents[catIdx].length === 1}
                          onAddNewItem={(newItemData) => addInventoryItem.mutateAsync(newItemData)}
                        />
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-primary gap-1 h-7 px-2"
                        onClick={() => addCoreComp(catIdx)}
                      >
                        <Plus className="w-3 h-3" /> Tambah {cat.replace('_', ' ').charAt(0).toUpperCase() + cat.replace('_', ' ').slice(1)} Lain
                      </Button>
                    </div>
                  );
                })}
                <Button
                  className="w-full h-10 font-semibold bg-green-600 hover:bg-green-700 mt-2"
                  onClick={saveComponents}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Menyimpan...' : '✓ Simpan Komponen'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {order.status === 'maserasi' && (
        <MaserasiTimer
          order={order}
          onTimerComplete={() => {
            createFillingTaskIfNeeded(order).then(() => {
              queryClient.invalidateQueries({ queryKey: ['production-order', id] });
              queryClient.invalidateQueries({ queryKey: ['production-tasks'] });
            });
          }}
        />
      )}

      {order.products?.length > 0 && (
        <div className="bg-white p-3.5 md:p-4 rounded-2xl border border-slate-200/90 shadow-2xs mt-6 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Layers className="w-4 h-4 text-primary" />
              <span className="font-bold text-sm text-slate-800">
                Daftar Varian & Komponen ({order.products.length} SKU)
              </span>
              {variantSearch && (
                <Badge className="bg-primary/10 text-primary text-[10px] font-bold">
                  {displayedProducts.length} Ditemukan
                </Badge>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Cari varian / SKU / ukuran ml..."
                  value={variantSearch}
                  onChange={(e) => setVariantSearch(e.target.value)}
                  className="pl-8 pr-7 h-8 text-xs bg-slate-50 border-slate-200 rounded-xl font-medium focus:bg-white transition-colors"
                />
                {variantSearch && (
                  <button
                    type="button"
                    onClick={() => setVariantSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Tombol Cetak Label Ruahan */}
              <Button
                type="button"
                onClick={() => setIsCetakRuahanOpen(true)}
                className="h-8 px-3 rounded-xl bg-[#302621] hover:bg-[#43352e] text-white text-xs font-bold shrink-0 flex items-center justify-center gap-1.5 shadow-xs cursor-pointer transition-all"
                title="Cetak Label Sampel Ruahan untuk semua atau varian tertentu (40x20mm)"
              >
                <Printer className="w-3.5 h-3.5 text-amber-400" />
                <span>Cetak Label Ruahan</span>
              </Button>
            </div>
          </div>

          {/* Tab Filter Status Cepat */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden pt-1 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setVariantStatusFilter('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                variantStatusFilter === 'all'
                  ? 'bg-[#302621] text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
              }`}
            >
              Semua <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${variantStatusFilter === 'all' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>{variantCounts.all}</span>
            </button>

            <button
              type="button"
              onClick={() => setVariantStatusFilter('waiting')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                variantStatusFilter === 'waiting'
                  ? 'bg-slate-700 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
              }`}
            >
              Belum Dikerjakan <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${variantStatusFilter === 'waiting' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'}`}>{variantCounts.waiting}</span>
            </button>

            <button
              type="button"
              onClick={() => setVariantStatusFilter('in_progress')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                variantStatusFilter === 'in_progress'
                  ? 'bg-amber-600 text-white shadow-xs'
                  : 'bg-amber-50 text-amber-800 border border-amber-200/60 hover:bg-amber-100'
              }`}
            >
              Sedang Dikerjakan <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${variantStatusFilter === 'in_progress' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-900'}`}>{variantCounts.in_progress}</span>
            </button>

            <button
              type="button"
              onClick={() => setVariantStatusFilter('done')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 ${
                variantStatusFilter === 'done'
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-emerald-50 text-emerald-800 border border-emerald-200/60 hover:bg-emerald-100'
              }`}
            >
              Selesai / Dikirim <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${variantStatusFilter === 'done' ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-900'}`}>{variantCounts.done}</span>
            </button>
          </div>
        </div>
      )}

      {order.products?.length > 0 ? (
        <div className="space-y-4 md:space-y-6 mt-4 md:mt-6">
          {displayedProducts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-200">
              <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-600">Tidak ada varian yang cocok dengan kata kunci "{variantSearch}"</p>
              <Button variant="ghost" size="sm" onClick={() => setVariantSearch('')} className="mt-2 text-xs text-primary font-bold">
                Reset Pencarian
              </Button>
            </div>
          ) : (
            displayedProducts.map((prod) => {
              const idx = prod._originalIndex !== undefined ? prod._originalIndex : (prod._id ? order.products.findIndex(p => p._id === prod._id) : enrichedProducts.indexOf(prod));
              const variantKey = prod._uniqueKey || (prod._id ? String(prod._id) : `variant-${idx}`);
              const isExpanded = expandedVariant === variantKey || expandedVariant === idx;
              const vStatus = getVariantStatus(prod);
              return (
                <Card key={variantKey} className="overflow-hidden border border-border shadow-sm">
                  <div
                    className="flex items-center gap-2 md:gap-4 p-2.5 md:p-4 cursor-pointer hover:bg-muted/30 transition-colors bg-gradient-to-r from-primary/5 to-transparent border-l-[3px] md:border-l-4 border-l-primary"
                    onClick={() => setExpandedVariant(isExpanded ? null : variantKey)}
                  >
                    <div className="bg-primary text-primary-foreground w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-xs md:text-sm shrink-0 shadow-sm">
                      {idx + 1}
                    </div>
                  <div className="flex-1 min-w-0 flex items-center gap-1.5 md:gap-2 flex-wrap">
                    <h2 className="font-display text-sm md:text-xl font-bold pr-1 flex flex-wrap items-center gap-1.5">
                      <span className="truncate">{((prod.product_name || prod.nama_product || prod.variant_name || prod.nama_varian || (prod.variant_id ? prod.variant_id.replace(/^.*?\s*-\s*/, '') : '')) || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim()}</span>
                      <span>{(() => {
                        const strippedName = ((prod.product_name || prod.nama_product || prod.variant_name || prod.nama_varian || (prod.variant_id ? prod.variant_id.replace(/^.*?\s*-\s*/, '') : '')) || '').replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim().toLowerCase();
                        const dbBahan = databaseBahanList.find(b => 
                          (b.nama_varian || '').trim().toLowerCase() === strippedName && 
                          (b.nama_brand || '').trim().toLowerCase() === (order.brand_name || '').split('/')[0].trim().toLowerCase()
                        );
                        const labelSize = prod.botol_ukuran_aktual || prod.botol_ukuran_label || prod.botol_ukuran_client || prod.ukuran_botol_ml || dbBahan?.botol_ukuran_label_ml || 0;
                        const actual = prod.botol_ukuran_aktual || labelSize;
                        const sizeStr = labelSize ? `${labelSize}ml` : (actual ? `${actual}ml` : '');
                        const actualStr = actual > 0 && String(actual) !== String(labelSize) ? ` (Isi ${actual}ml)` : '';
                        return sizeStr ? ` (${sizeStr}${actualStr})` : '';
                      })()}</span>
                      {prod.is_updated && (
                        <span className="px-1.5 md:px-2 py-0.5 rounded-full text-[8px] md:text-[10px] font-black uppercase tracking-wider border bg-rose-50 text-rose-600 border-rose-200 shrink-0">Baru Diupdate</span>
                      )}
                    </h2>

                    {/* Badge Persentase Kesiapan Bahan & Komponen per Varian */}
                    {(() => {
                      const vComps = (prod.enrichedCore || []).flat().filter(c => c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name || c.name);
                      const vReady = vComps.filter(c => c.status === 'ready' || c.status === 'ready_client').length;
                      const vTotal = vComps.length;
                      const vPct = vTotal > 0 ? Math.round((vReady / vTotal) * 100) : 100;

                      if (order.status === 'done' || order.is_shipped) return null;

                      if (vPct === 100) {
                        return (
                          <Badge variant="outline" className="text-[8px] md:text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300 font-bold flex items-center gap-1 shadow-2xs">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            100% Siap Produksi
                          </Badge>
                        );
                      }
                      return (
                        <Badge variant="outline" className="text-[8px] md:text-[10px] bg-amber-50 text-amber-800 border-amber-300 font-bold flex items-center gap-1 shadow-2xs">
                          <Clock className="w-3 h-3 text-amber-600" />
                          {vPct}% Bahan Siap ({vReady}/{vTotal})
                        </Badge>
                      );
                    })()}

                    <Badge variant="outline" className={`text-[8px] md:text-[10px] whitespace-nowrap px-1.5 md:px-2.5 ${vStatus.color}`}>
                      {vStatus.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                    {(() => {
                      const rejectsInfo = getVariantRejects(prod.product_name || prod.variant_name);
                      const targetQty = Number(prod.qty) || Number(prod.target_qty) || 0;
                      const validTarget = Math.max(0, targetQty - rejectsInfo.totalRejects);
                      
                      const rawName = prod.product_name || prod.variant_name || 'Produk Utama';
                      const strippedName = rawName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
                      const labelSize = prod.botol_ukuran_aktual || prod.botol_ukuran_label || prod.botol_ukuran_client || prod.ukuran_botol_ml || 0;
                      const vName = labelSize ? `${strippedName} (${labelSize}ml)` : rawName;
                      
                      const cleanRaw = (rawName || '').toLowerCase().trim();
                      const cleanVName = (vName || '').toLowerCase().trim();
                      const racikanBahan = (prod.racikan_digunakan || prod.racikan || []).map(r => (r.nama_bahan || '').toLowerCase().trim());
                      
                      const pTasks = allTasks.filter(t => {
                        const tProd = (t.product_name || '').toLowerCase().trim();
                        const tTask = (t.task_name || '').toLowerCase().trim();
                        if (tProd === cleanVName || tProd === cleanRaw || tTask.includes(cleanVName) || tTask.includes(cleanRaw)) return true;
                        if (t.variants && t.variants.some(v => (v || '').toLowerCase().trim() === cleanVName || (v || '').toLowerCase().trim() === cleanRaw)) return true;
                        const hasBibit = racikanBahan.some(bahan => {
                          if (!bahan) return false;
                          const words = bahan.split(/\s+/).filter(w => w.length > 3 && !/^\d+$/.test(w) && !['parf', 'plus', 'mkt', 'oil'].includes(w));
                          return words.length > 0 && words.some(w => tProd.includes(w) || tTask.includes(w));
                        });
                        if (hasBibit) return true;
                        return !t.product_name;
                      });
                      
                      let completedRacik = 0, completedFilling = 0, completedPacking = 0;
                      pTasks.forEach(task => {
                        let completedQty = 0;
                        if (task.member_progress) {
                          Object.values(task.member_progress).forEach(prog => {
                            if (prog.status === 'completed' && prog.items) {
                               prog.items.forEach(item => {
                                  if (item.qty > 0) completedQty += Number(item.qty);
                               });
                            }
                          });
                        } else if (['completed'].includes(task.status)) {
                          completedQty = Number(task.final_quantity) || Number(task.target_quantity) || 0;
                        }
                        
                        if (task.activity_type === 'racik') completedRacik += completedQty;
                        else if (task.activity_type === 'filling') completedFilling += completedQty;
                        else if (task.activity_type === 'packing') completedPacking += completedQty;
                      });

                      const renderStageProgress = (label, completed) => {
                        if (completed === 0) return null;
                        const remaining = Math.max(0, validTarget - completed);
                        if (remaining === 0) return <span className="text-green-600 font-bold ml-1">{label} Selesai</span>;
                        return <span className="text-amber-600 ml-1">{label} {completed} (Kurang {remaining})</span>;
                      };

                      return (
                        <div className="flex flex-col md:flex-row items-end md:items-center gap-1 md:gap-2">
                          {rejectsInfo.totalRejects > 0 && (
                            <Badge variant="outline" className="text-[8px] md:text-xs bg-red-50 text-red-600 border-red-200">
                              Reject: {rejectsInfo.totalRejects} <span className="hidden md:inline">({rejectsInfo.activities})</span>
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-[8px] md:text-xs bg-background md:bg-white shadow-sm flex gap-2 items-center">
                            <span>Target: {validTarget} pcs</span>
                            {(completedRacik > 0 || completedFilling > 0 || completedPacking > 0) && <span className="text-slate-300">|</span>}
                            {renderStageProgress('Mixing', completedRacik)}
                            {completedFilling > 0 && <span className="text-slate-300">|</span>}
                            {renderStageProgress('Filling', completedFilling)}
                            {completedPacking > 0 && <span className="text-slate-300">|</span>}
                            {renderStageProgress('Pengemasan', completedPacking)}
                          </Badge>
                        </div>
                      );
                    })()}
                    {canEdit && editingVariantIdx !== idx && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 md:h-8 md:w-8 hover:bg-muted print:hidden" onClick={(e) => {
                         e.stopPropagation();
                         setExpandedVariant(variantKey);
                         
                         const parsed = parseCoreComponents(prod.components);
                         const targetQty = Number(prod.target_qty || prod.qty || 1);
                         const botolSize = Number(prod.botol_ukuran_aktual || prod.ukuran_botol_ml || 1);
                         const racikanDigunakan = order?.racikan_digunakan || [];
                         const totalVol = targetQty * botolSize;
                         
                         const enrichedParsed = parsed.map((catArray, catIdx) => {
                           const catName = CORE_CATEGORIES[catIdx];
                           return catArray.map(comp => {
                             let qNeeded = Number(comp.qty_needed) || 0;
                             
                             if (['botol', 'tutup', 'spray'].includes(catName)) {
                               if (qNeeded !== targetQty) qNeeded = targetQty;
                             } else if (['bibit', 'bahan_cair', 'tambahan'].includes(catName) && qNeeded === 0) {
                               const rMatch = racikanDigunakan.find(r => r.nama_bahan?.toLowerCase() === comp.item_name?.toLowerCase());
                               if (rMatch && rMatch.persentase > 0) {
                                 const kMl = totalVol * rMatch.persentase / 100;
                                 const u = (comp.unit || '').toLowerCase();
                                 if (u === 'kg' || u === 'l' || u === 'liter') {
                                   qNeeded = kMl / 1000;
                                 } else {
                                   qNeeded = kMl;
                                 }
                               }
                             }
                             
                             return {
                               ...comp,
                               qty_needed: Number(qNeeded.toFixed(3)),
                               base_qty_needed: Number(qNeeded.toFixed(3)),
                               base_target_qty: targetQty,
                               base_botol_size: botolSize
                             };
                           });
                         });
                         
                         setCoreComponents(enrichedParsed);
                         setEditingVariantIdx(idx);
                         setVariantEditForm({
                           product_name: prod.product_name || prod.variant_name || '',
                           target_qty: targetQty,
                           botol_ukuran_aktual: botolSize,
                         });
                      }}>
                         <Edit2 className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                      </Button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground print:hidden" /> : <ChevronDown className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground print:hidden" />}
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="p-5 pt-0 mt-4 space-y-6">

                    {/* Komponen Utama Variant */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-3 border-b pb-2">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-primary" />
                          <h3 className="font-semibold text-sm uppercase tracking-wide">Komponen Utama</h3>
                        </div>
                        {editingVariantIdx === idx && (
                          <div className="flex gap-2">
                            <Button size="sm" className="gap-1.5 text-xs h-7 bg-green-600 hover:bg-green-700" onClick={saveComponents} disabled={updateMutation.isPending}>
                              <Save className="w-3 h-3" /> Simpan
                            </Button>
                            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setEditingVariantIdx(null)}>
                              Batal
                            </Button>
                          </div>
                        )}
                      </div>

                      {editingVariantIdx === idx ? (
                        <div className="space-y-4">
                          {/* Info Variant Form */}
                          <div className="bg-slate-50/50 p-4 rounded-lg border border-slate-100 space-y-3 mb-6">
                            <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wide">Info Variant</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                 <Label className="text-[10px] md:text-xs text-slate-500">Nama Variant</Label>
                                 <Input value={variantEditForm?.product_name || ''} onChange={e => setVariantEditForm({...variantEditForm, product_name: e.target.value})} className="h-8 text-xs mt-1" />
                              </div>
                              <div>
                                 <Label className="text-[10px] md:text-xs text-slate-500">Target Qty</Label>
                                 <Input type="number" value={variantEditForm?.target_qty || ''} onChange={e => {
                                   const newQty = Number(e.target.value) || 0;
                                   const currentBotolSize = Number(variantEditForm?.botol_ukuran_aktual) || 1;
                                   setVariantEditForm({...variantEditForm, target_qty: newQty});
                                   if (coreComponents) {
                                     setCoreComponents(coreComponents.map((catArray, catIdx) => {
                                       const catName = CORE_CATEGORIES[catIdx];
                                       return catArray.map(comp => {
                                         if (['botol', 'tutup', 'spray'].includes(catName)) {
                                           return { ...comp, qty_needed: newQty };
                                         } else if (['bibit', 'bahan_cair', 'tambahan'].includes(catName)) {
                                           const baseTarget = comp.base_target_qty > 0 ? comp.base_target_qty : 1;
                                           const baseSize = comp.base_botol_size > 0 ? comp.base_botol_size : 1;
                                           const baseQty = comp.base_qty_needed || 0;
                                           
                                           const ratioQty = newQty / baseTarget;
                                           const ratioSize = currentBotolSize / baseSize;
                                           const finalQty = baseQty * ratioQty * ratioSize;
                                           
                                           return { ...comp, qty_needed: Number(finalQty.toFixed(2)) };
                                         }
                                         return comp;
                                       });
                                     }));
                                   }
                                 }} className="h-8 text-xs mt-1" />
                              </div>
                              <div>
                                 <Label className="text-[10px] md:text-xs text-slate-500">Ukuran Botol (ml/gr)</Label>
                                 <Input type="number" value={variantEditForm?.botol_ukuran_aktual || ''} onChange={e => {
                                   const newSize = Number(e.target.value) || 0;
                                   const currentTargetQty = Number(variantEditForm?.target_qty) || 1;
                                   setVariantEditForm({...variantEditForm, botol_ukuran_aktual: newSize});
                                   if (coreComponents) {
                                     setCoreComponents(coreComponents.map((catArray, catIdx) => {
                                       const catName = CORE_CATEGORIES[catIdx];
                                       return catArray.map(comp => {
                                         if (['bibit', 'bahan_cair'].includes(catName)) {
                                           const baseTarget = comp.base_target_qty > 0 ? comp.base_target_qty : 1;
                                           const baseSize = comp.base_botol_size > 0 ? comp.base_botol_size : 1;
                                           const baseQty = comp.base_qty_needed || 0;
                                           
                                           const ratioQty = currentTargetQty / baseTarget;
                                           const ratioSize = newSize / baseSize;
                                           const finalQty = baseQty * ratioQty * ratioSize;
                                           
                                           return { ...comp, qty_needed: Number(finalQty.toFixed(2)) };
                                         }
                                         return comp;
                                       });
                                     }));
                                   }
                                 }} className="h-8 text-xs mt-1" placeholder="Misal: 55" />
                              </div>
                            </div>
                          </div>

                          {CORE_CATEGORIES.map((cat, catIdx) => {
                            const isHidden = !['botol', 'tutup', 'spray', 'bibit'].includes(cat) && coreComponents[catIdx].length === 0;
                            if (isHidden) return null;
                            return (
                              <div key={cat} className="space-y-2">
                                {coreComponents[catIdx].map((comp, compIdx) => (
                                  <POComponentItem
                                    key={`${cat}-${compIdx}`}
                                    comp={comp}
                                    inventoryItems={inventoryItems}
                                    onChange={(updated) => updateCoreComp(catIdx, compIdx, updated)}
                                    onRemove={() => removeCoreComp(catIdx, compIdx)}
                                    isCore={['botol', 'tutup', 'spray'].includes(cat) && coreComponents[catIdx].length === 1}
                                    onAddNewItem={(newItemData) => addInventoryItem.mutateAsync(newItemData)}
                                  />
                                ))}
                                <Button type="button" variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-primary gap-1 h-7 px-2" onClick={() => addCoreComp(catIdx)}>
                                  <Plus className="w-3 h-3" /> Tambah {cat.replace('_', ' ').charAt(0).toUpperCase() + cat.replace('_', ' ').slice(1)} Lain
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                      <div className="space-y-2">
                        {CORE_CATEGORIES.map((cat, catIdx) => {
                          const comps = (prod.enrichedCore[catIdx] || []).filter(c => c.inventory_item_id || ['pending_client', 'ready_client'].includes(c.status) || c.item_name);
                          const catLabel = cat.charAt(0).toUpperCase() + cat.slice(1);
                          if (!comps || comps.length === 0) return null;
                          return (
                            <div key={cat} className="space-y-1.5">
                              {comps.map((comp, i) => {
                                const isFromClient = ['pending_client', 'ready_client'].includes(comp.status);
                                return (
                                <div key={i} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${isFromClient ? 'bg-sky-50 border-sky-200' : `bg-white ${catColor[cat]}`}`}>
                                  <div className="w-7 h-7 bg-white rounded-md shadow-sm border flex items-center justify-center shrink-0">
                                    {isFromClient ? <User className="w-4 h-4 text-sky-600" /> : catIcon[cat]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm font-semibold truncate">{comp.item_name || catLabel}</p>
                                      {isFromClient && (
                                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 font-semibold shrink-0">
                                          <User className="w-3 h-3" /> Dari Client
                                        </span>
                                      )}
                                    </div>
                                    {comp.vendor && <p className="text-[10px] opacity-70 truncate">{comp.vendor}</p>}
                                  </div>
                                  {!isFromClient && (
                                    <div className="text-right shrink-0">
                                      {(() => {
                                        const { display: qDisp, unit: qUnit } = smartDisplayQty(comp.qty_needed || 0, comp.unit || 'pcs');
                                        return <p className="text-xs font-medium">{qDisp} {qUnit}</p>;
                                      })()}
                                      {(() => {
                                        const { display, unit: du } = smartDisplayQty(comp.stock_available || 0, comp.unit || 'pcs');
                                        return <p className="text-[10px] opacity-70">Stok: {display} {du}</p>;
                                      })()}
                                    </div>
                                  )}
                                  {isFromClient ? (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const newStatus = comp.status === 'ready_client' ? 'pending_client' : 'ready_client';
                                        const newComps = [...(order.components || [])];
                                        const matchIdx = newComps.findIndex(c => c.category === cat && c.item_name === comp.item_name && ['pending_client', 'ready_client'].includes(c.status));
                                        if (matchIdx >= 0) {
                                          newComps[matchIdx] = { ...newComps[matchIdx], status: newStatus };
                                          const payload = { components: newComps };
                                          if (order.products?.length > 0) {
                                            payload.products = order.products.map(p => ({ ...p, components: newComps }));
                                          }
                                          await updateMutation.mutateAsync(payload);
                                          toast.success(newStatus === 'ready_client' ? '✅ Item dari client ditandai sudah sampai!' : 'Item dikembalikan ke menunggu');
                                        }
                                      }}
                                      className={`inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full border font-semibold transition-all shrink-0 cursor-pointer
                                        ${comp.status === 'ready_client'
                                          ? 'bg-teal-100 text-teal-700 border-teal-300 hover:bg-teal-200'
                                          : 'bg-white text-sky-700 border-sky-300 hover:bg-sky-50 hover:border-sky-400 shadow-sm'}`}
                                    >
                                      {comp.status === 'ready_client'
                                        ? <><CheckCircle2 className="w-3 h-3" /> Sudah Sampai</>
                                        : <><Package className="w-3 h-3" /> Tandai Sampai</>}
                                    </button>
                                  ) : (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${scColor[comp.status] || scColor.pending}`}>
                                      {comp.status === 'ready' ? 'Ready' : comp.status === 'need_buy' ? 'Habis di Inventori' : 'Pending'}
                                    </span>
                                  )}
                                </div>
                              );})}
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>



                    {/* Racikan Formula */}
                    <div className="pt-2">
                      <div className="flex items-center gap-2 mb-3 border-b pb-2">
                        <Droplets className="w-4 h-4 text-primary" />
                        <h3 className="font-semibold text-sm uppercase tracking-wide">Racikan Formula</h3>
                      </div>
                      <RacikanFormulaSection
                        order={{ ...order, ...prod }}
                        isSaving={updateMutation.isPending}
                        onSave={async (newRacikan) => {
                          const newProducts = [...order.products];
                          newProducts[idx].racikan_digunakan = newRacikan;
                          await updateMutation.mutateAsync({
                            products: newProducts,
                            racikan_digunakan: newProducts.flatMap(p => p.racikan_digunakan)
                          });
                        }}
                      />
                    </div>

                    {/* Item Tambahan Variant */}
                    <div className="pt-4 border-t border-slate-100">
                      <AdditionalComponentSection
                        addons={prod.komponen_tambahan || []}
                        onChange={async (newAddons) => {
                          const newProducts = [...order.products];
                          newProducts[idx].komponen_tambahan = newAddons;
                          await updateMutation.mutateAsync({ products: newProducts });
                        }}
                        isShipped={order.is_shipped}
                      />
                    </div>

                    {/* Artwork & Desain Kemasan Preview */}
                    {(() => {
                      const dbBahan = databaseBahanList.find(b => b.nama_varian === prod.product_name && b.nama_brand === order.brand_name.split('/')[0].trim());
                      return (
                        <div className="pt-4 border-t border-slate-100">
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="font-semibold text-sm uppercase tracking-wide">Artwork & Referensi Desain</h3>
                          </div>
                          <ClickToPreviewImage src={dbBahan?.image_url} alt={`Artwork ${prod.product_name}`} />
                        </div>
                      );
                    })()}
                  </CardContent>
                )}
              </Card>
            );
          }))}

          {/* Tugas Produksi Global (semua variant) */}
          <div className="mt-8">
            <TambahTugasSection order={order} allTasks={allTasks} />
          </div>
        </div>
      ) : (
        <>
          <TambahTugasSection order={order} allTasks={allTasks} />

          <RacikanFormulaSection
            order={order}
            isSaving={updateMutation.isPending}
            onSave={async (newRacikan) => {
              await updateMutation.mutateAsync({ racikan_digunakan: newRacikan });
            }}
          />
          <div className="mt-6">
            <AdditionalComponentSection
              addons={addons}
              onChange={handleAddonsChange}
              isShipped={false}
            />
          </div>
        </>
      )}

      {/* Modal Selesai Kirim / Atur Pengiriman */}
      {showSelesaiModal && (
        <SelesaiKirimModal
          open={showSelesaiModal}
          onCancel={() => {
            setShowSelesaiModal(false);
            setTimeout(() => setIsEditingShipping(false), 200); // Clear after animation
          }}
          onConfirm={async (data) => {
            await handleAturPengiriman(data);
            setIsEditingShipping(false);
          }}
          onDeleteShipping={handleHapusSuratJalan}
          isLoading={markingDone}
          order={order}
          allTasks={allTasks}
          isEditing={isEditingShipping}
        />
      )}

      {/* Riwayat Penggunaan Bahan — Dikategorikan per Kategori */}
      {(() => {
        const aggregatedConsumed = {};

        // Helper untuk mengecek tahapan yang benar-benar dikerjakan / selesai per varian
        const getConsumedCategoriesForVariant = (prod) => {
          const normOrderStatus = String(order?.status || '').toLowerCase();
          
          // Jika PO sudah selesai sepenuhnya atau dalam pengiriman
          if (['done', 'selesai', 'siap_kirim', 'shipping', 'dalam_pengiriman'].some(s => normOrderStatus.includes(s))) {
            return new Set(['bibit', 'pelarut', 'alkohol', 'campuran', 'botol', 'tutup', 'spray', 'box', 'stiker', 'packaging', 'tambahan']);
          }

          const consumedStages = new Set();

          // Jika PO secara global sudah mencapai tahap Pengemasan -> Racik & Filling pasti sudah selesai
          if (['packing', 'pengemasan'].some(s => normOrderStatus.includes(s))) {
            ['bibit', 'pelarut', 'alkohol', 'campuran', 'botol', 'tutup', 'spray', 'box', 'stiker', 'packaging'].forEach(c => consumedStages.add(c));
          } else if (['filling', 'maserasi', 'menunggu_packing'].some(s => normOrderStatus.includes(s))) {
            ['bibit', 'pelarut', 'alkohol', 'campuran', 'botol', 'tutup', 'spray'].forEach(c => consumedStages.add(c));
          }

          // Cek tugas spesifik untuk varian ini
          const vName = getProductDisplayName(prod);
          const rawName = prod?.product_name || prod?.variant_name;
          const vTasks = allTasks.filter(t => 
            t.product_name === vName || 
            t.product_name === rawName || 
            (t.variants && (t.variants.includes(vName) || t.variants.includes(rawName))) ||
            (!t.product_name && (!t.variants || t.variants.length === 0))
          );

          vTasks.forEach(t => {
            const isTaskActiveOrDone = ['in_progress', 'berjalan', 'completed', 'selesai', 'waiting_qc', 'menunggu_qc'].includes((t.status || '').toLowerCase());
            if (isTaskActiveOrDone) {
              const act = (t.activity_type || t.task_name || '').toLowerCase();
              if (act.includes('racik') || act.includes('mixing')) {
                ['bibit', 'pelarut', 'alkohol', 'campuran'].forEach(c => consumedStages.add(c));
              }
              if (act.includes('filling')) {
                ['bibit', 'pelarut', 'alkohol', 'campuran', 'botol', 'tutup', 'spray'].forEach(c => consumedStages.add(c));
              }
              if (act.includes('pack') || act.includes('pengemasan') || act.includes('label') || act.includes('seal')) {
                ['bibit', 'pelarut', 'alkohol', 'campuran', 'botol', 'tutup', 'spray', 'box', 'stiker', 'packaging'].forEach(c => consumedStages.add(c));
              }
            }
          });

          return consumedStages;
        };

        // Helper untuk mendapatkan racikan formula murni milik varian tertentu
        const getVariantRacikan = (prod) => {
          if (Array.isArray(prod.racikan) && prod.racikan.length > 0) return prod.racikan;
          if (Array.isArray(prod.racikan_digunakan) && prod.racikan_digunakan.length > 0) return prod.racikan_digunakan;

          const vName = (prod.product_name || prod.variant_name || '').toLowerCase().trim();
          const strippedVName = vName.replace(/\s*\(\s*\d+\s*ml\s*\)/i, '').trim();
          const brandName = (order?.brand_name || '').toLowerCase().trim();

          // 1. Cari dari databaseBahanList
          const matchedBahan = databaseBahanList.find(b => {
            const bVarian = (b.nama_varian || '').toLowerCase().trim();
            const bBrand = (b.nama_brand || '').toLowerCase().trim();
            return (bVarian === vName || bVarian === strippedVName) && (!bBrand || brandName.includes(bBrand) || bBrand.includes(brandName));
          });
          if (Array.isArray(matchedBahan?.racikan) && matchedBahan.racikan.length > 0) return matchedBahan.racikan;

          // 2. Cari dari racikanDbList
          const matchedDb = racikanDbList.find(db => {
            const dbVarian = (db.nama_varian || '').toLowerCase().trim();
            const dbBrand = (db.nama_brand || '').toLowerCase().trim();
            return (dbVarian === vName || dbVarian === strippedVName) && (!dbBrand || brandName.includes(dbBrand) || dbBrand.includes(brandName));
          });
          if (Array.isArray(matchedDb?.racikan) && matchedDb.racikan.length > 0) return matchedDb.racikan;

          // 3. Jika hanya 1 varian di PO dan ada order.racikan_digunakan
          if (order?.products?.length === 1 && Array.isArray(order.racikan_digunakan) && order.racikan_digunakan.length > 0) {
            return order.racikan_digunakan;
          }

          return [];
        };

        const processProd = (prod) => {
          const consumedCats = getConsumedCategoriesForVariant(prod);
          if (consumedCats.size === 0) return;

          const targetQty = Number(prod.target_qty || prod.qty || order?.target_qty || 1);
          const botolSize = Number(prod.botol_ukuran_aktual || prod.botol_ukuran_label || prod.botol_ukuran_client || prod.ukuran_botol_ml || 30);
          const totalVolMl = targetQty * botolSize;

          // 1. Proses Racikan Formula (Bibit & Pelarut) Murni Milik Varian Ini
          const racikanList = getVariantRacikan(prod);
          if (Array.isArray(racikanList) && racikanList.length > 0 && (consumedCats.has('bibit') || consumedCats.has('pelarut'))) {
            racikanList.forEach(r => {
              const bName = (r.bahan_nama || r.nama_bahan || r.nama || '').trim();
              if (!bName) return;

              const persentase = parseFloat(String(r.persentase).replace(',', '.')) || 0;
              const mlNeeded = (persentase / 100) * totalVolMl;
              const rawCat = (r.kategori || r.kategori_bahan || 'Bibit').toLowerCase();

              let category = 'Bibit Parfum';
              let unit = 'kg';
              let displayQty = mlNeeded / 1000; // Standar bibit dalam kg

              if (rawCat.includes('alkohol') || rawCat.includes('pelarut') || bName.toLowerCase().includes('superfine') || bName.toLowerCase().includes('supertex') || bName.toLowerCase().includes('aqua')) {
                category = 'Pelarut & Alkohol';
                unit = 'L';
                displayQty = mlNeeded / 1000;
              } else if (rawCat.includes('dpg') || rawCat.includes('peg') || bName.toLowerCase().includes('dpg') || bName.toLowerCase().includes('peg')) {
                category = 'Campuran & Pengikat';
                unit = 'kg';
                displayQty = mlNeeded / 1000;
              }

              const key = `${category}_${bName}`;
              if (!aggregatedConsumed[key]) {
                aggregatedConsumed[key] = {
                  category,
                  item_name: bName,
                  unit,
                  qty_needed: 0
                };
              }
              aggregatedConsumed[key].qty_needed += Number(displayQty.toFixed(2));
            });
          }

          // 2. Proses Kemasan Fisik (Botol, Tutup, Spray, Box, Stiker)
          const comps = (prod.enrichedCore || []).flat();
          comps.forEach(comp => {
            if (!comp || !comp.item_name) return;
            const cat = String(comp.category || '').toLowerCase();
            
            // Jangan dobel proses bibit/bahan_cair dari comps jika sudah diproses di racikan
            if (['bibit', 'bahan_cair', 'bahan', 'pelarut'].includes(cat)) return;

            const isConsumed = Array.from(consumedCats).some(c => cat.includes(c) || c.includes(cat));
            if (isConsumed) {
              const invItem = inventoryItems.find(i => String(i.id) === String(comp.inventory_item_id) || i.item_name === comp.item_name);
              let displayCategory = 'Kemasan & Packaging';
              if (cat.includes('botol')) displayCategory = 'Botol';
              else if (cat.includes('tutup')) displayCategory = 'Tutup';
              else if (cat.includes('spray')) displayCategory = 'Spray / Pump';
              else if (cat.includes('box') || cat.includes('kemasan')) displayCategory = 'Box Kemasan';
              else if (cat.includes('stiker') || cat.includes('label')) displayCategory = 'Stiker & Label';

              const key = `${displayCategory}_${comp.item_name}`;
              if (!aggregatedConsumed[key]) {
                aggregatedConsumed[key] = {
                  category: displayCategory,
                  item_name: comp.item_name,
                  unit: 'pcs',
                  qty_needed: 0
                };
              }
              // Gunakan targetQty varian untuk botol/tutup
              const qPcs = Number(comp.qty_needed) > 0 && Number(comp.qty_needed) <= targetQty ? Number(comp.qty_needed) : targetQty;
              aggregatedConsumed[key].qty_needed += qPcs;
            }
          });
        };

        if (enrichedProducts && enrichedProducts.length > 0) {
          enrichedProducts.forEach(prod => {
            processProd(prod);
          });
        } else if (enrichedCore && enrichedCore.length > 0) {
          const globalProd = { enrichedCore, racikan: order?.racikan_digunakan, target_qty: order?.target_qty || 1 };
          processProd(globalProd);
        }

        const consumedList = Object.values(aggregatedConsumed);
        if (consumedList.length === 0) return null;

        // Group by category
        const groupedByCategory = {};
        consumedList.forEach(comp => {
          if (!groupedByCategory[comp.category]) {
            groupedByCategory[comp.category] = [];
          }
          groupedByCategory[comp.category].push(comp);
        });

        const categoryKeys = Object.keys(groupedByCategory);

        return (
          <Card className="border-t-4 border-t-emerald-500 mt-6 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg flex items-center gap-2 text-emerald-950 font-bold">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                Riwayat Penggunaan Bahan Baku
              </CardTitle>
              <p className="text-xs text-slate-500 font-medium">
                Bahan baku & kemasan di bawah ini sudah digunakan untuk tahapan varian yang sedang aktif / selesai dikerjakan pada PO ini.
              </p>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="space-y-5">
                {categoryKeys.map((cat, ci) => (
                  <div key={ci}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center justify-center px-2.5 h-6 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                        {cat}
                      </span>
                    </div>
                    <div className="space-y-1.5 pl-2">
                      {groupedByCategory[cat].map((comp, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2.5 bg-emerald-50/60 rounded-xl border border-emerald-200/60">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-800">{comp.item_name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-black text-rose-600">
                              -{comp.qty_needed.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {comp.unit}
                            </div>
                            <div className="text-[10px] text-slate-400 font-semibold">Telah Terpakai</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Surat Jalan Print Preview */}
      {suratJalanToPrint && (
        <SuratJalanPrint
          order={order}
          shipment={suratJalanToPrint.type === 'shipment' ? suratJalanToPrint.shipment : null}
          companySettings={companySettings}
          onClose={() => setSuratJalanToPrint(null)}
        />
      )}

      {/* Modal Pilihan Dokumen Cetak dari Asa */}
      <AsaPrintChoiceModal
        isOpen={Boolean(asaPrintTarget)}
        onClose={() => setAsaPrintTarget(null)}
        order={asaPrintTarget?.order || order}
        shipment={asaPrintTarget?.shipment}
        onSelectSuratJalan={() => {
          if (asaPrintTarget?.isLegacy) {
            setSuratJalanToPrint({ type: 'legacy', shipment: asaPrintTarget.shipment });
          } else {
            setSuratJalanToPrint({ type: 'shipment', shipment: asaPrintTarget?.shipment });
          }
        }}
        onSelectLabelDus={() => {
          setLabelDusToPrint({
            order: asaPrintTarget?.order || order,
            shipment: asaPrintTarget?.isLegacy ? null : asaPrintTarget?.shipment,
            initialMode: 'labels'
          });
        }}
        onSelectBoth={() => {
          setLabelDusToPrint({
            order: asaPrintTarget?.order || order,
            shipment: asaPrintTarget?.isLegacy ? null : asaPrintTarget?.shipment,
            initialMode: 'both'
          });
        }}
      />

      {/* Label Dus Print Preview */}
      {labelDusToPrint && (
        <LabelDusPrintModal
          isOpen={!!labelDusToPrint}
          onClose={() => setLabelDusToPrint(null)}
          order={labelDusToPrint.order || order}
          shipment={labelDusToPrint.shipment}
          allTasks={allTasks}
          initialMode={labelDusToPrint.initialMode || 'labels'}
          initialSettings={{
            label_size: labelDusToPrint.shipment?.label_size || 'besar',
            tgl_produksi: labelDusToPrint.shipment?.tgl_produksi,
            exp_date: labelDusToPrint.shipment?.exp_date,
            pcs_per_dus: labelDusToPrint.shipment?.pcs_per_dus,
            catatan_dus: labelDusToPrint.shipment?.catatan_dus,
            variant_name: (labelDusToPrint.shipment?.items?.length > 1 || (order?.products?.length > 1)) ? 'all' : (labelDusToPrint.shipment?.items?.[0]?.variant_name || 'all')
          }}
        />
      )}

      {/* ASA Confirm Modal */}
      {showAsaConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60  animate-in fade-in duration-200 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-amber-100">
            <div className="bg-gradient-to-br from-[#FFFDF3] to-[#FDF3D8] p-8 flex flex-col items-center text-center relative overflow-hidden border-b border-[#F6E9CC]">
              {/* Soft elegant glow effect */}
              <div className="absolute top-[-50%] left-1/2 -translate-x-1/2 w-64 h-64 bg-white/40 blur-3xl rounded-full"></div>
              
              <div className="w-16 h-16 rounded-full bg-[#8C6208] flex items-center justify-center border-[4px] border-white mb-4 shadow-[0_8px_16px_rgba(140,98,8,0.2)] z-10">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-display font-bold text-[#6D4C06] relative z-10 tracking-wide">Halo! ASA di sini</h3>
            </div>
            <div className="p-6 md:p-8 text-slate-700">
              <p className="leading-relaxed mb-4">
                Wah, pesanan ini sudah sampai di tujuan ya? Hebat!
                <br />
                Jika Anda menekan selesai, ASA akan mengubah status PO dan Invoice ini menjadi <strong className="text-green-600">Selesai</strong>.
              </p>

              <div className="bg-amber-50/50 rounded-xl p-4 mb-6 border border-amber-100/50">
                <ul className="space-y-4 text-sm">
                  <li className="flex gap-3">
                    <div className="shrink-0 pt-0.5"><Package className="w-5 h-5 text-amber-600" /></div>
                    <span><strong>Soal stok, tenang saja!</strong> ASA sudah mengurangi otomatis setiap tahap progres (saat meracik, filing, dan pengemasan).</span>
                  </li>
                  <li className="flex gap-3">
                    <div className="shrink-0 pt-0.5"><FileText className="w-5 h-5 text-amber-600" /></div>
                    <span>Nantinya, Anda juga masih bisa mengunduh <strong>Surat Jalan</strong> kapan saja melalui halaman menu Selesai kok!</span>
                  </li>
                </ul>
              </div>

              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  onClick={() => setShowAsaConfirm(false)}
                >
                  Nanti Dulu
                </Button>
                <Button
                  className="flex-1 bg-[#8C6208] hover:bg-[#704e06] text-white shadow-lg shadow-amber-900/10"
                  onClick={executeSelesaiBarangSampai}
                  disabled={markingDone}
                >
                  Ya, Tandai Selesai!
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QC Audit Modal */}
      <QCAuditModal
        isOpen={qcModalState.isOpen}
        onClose={() => setQcModalState({ isOpen: false, variant: null, completedPacking: 0 })}
        variant={qcModalState.variant}
        order={order}
        completedPacking={qcModalState.completedPacking}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['production-order', id] });
        }}
      />

      {/* Modal Cetak Label Sampel Ruahan */}
      {isCetakRuahanOpen && (
        <CetakRuahanModal
          isOpen={isCetakRuahanOpen}
          onClose={() => setIsCetakRuahanOpen(false)}
          order={order}
        />
      )}

      {/* Modal Dialog Pilih Hapus Surat Jalan */}
      <Dialog open={showHapusSuratJalanModal} onOpenChange={setShowHapusSuratJalanModal}>
        <DialogContent className="max-w-md bg-white rounded-2xl p-5 border border-slate-200 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-rose-600" />
              <span>Pilih Surat Jalan yang Ingin Dihapus</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 mt-1 mb-3">
            PO ini memiliki <strong>{order?.shipments?.length || 0} destinasi pengiriman</strong>. Anda dapat menghapus salah satu destinasi saja tanpa menghapus destinasi lainnya, atau membatalkan seluruh pengiriman.
          </p>

          <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
            {(order?.shipments || []).map((shipment, sIdx) => {
              const totalItems = (shipment.items || []).reduce((sum, i) => sum + (Number(i.qty_akhir) || 0), 0);
              return (
                <div key={shipment.id || sIdx} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50 gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-xs text-slate-800">
                      Surat Jalan Destinasi {sIdx + 1}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                      Via {shipment.shipping_via || '-'} • {totalItems} pcs
                      {shipment.penerima ? ` • Penerima: ${shipment.penerima}` : ''}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 text-rose-600 border-rose-300 bg-rose-50 hover:bg-rose-100 font-bold shrink-0 gap-1"
                    onClick={() => handleHapusSingleShipment(sIdx)}
                    disabled={markingDone}
                  >
                    <Trash2 className="w-3 h-3 text-rose-600" /> Hapus
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-4 border-t border-slate-100 mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHapusSuratJalanModal(false)}
              className="text-xs text-slate-500"
            >
              Batal
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="text-xs font-bold gap-1.5"
              onClick={handleHapusAllSuratJalan}
              disabled={markingDone}
            >
              <Trash2 className="w-3.5 h-3.5" /> Hapus Semua Surat Jalan
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SPK Print & Batch Verification Modal */}
      {showSpkModal && order && (
        <SpkPrintModal
          isOpen={showSpkModal}
          onClose={() => setShowSpkModal(false)}
          invoice={spkInvoiceData}
          onPrintMulti={(selectedProducts, showNilai, spkDate) => {
            generateSpkPdf({
              invoiceCode: order.order_number || order.id,
              record: { ...spkInvoiceData, ...order, spk_date: spkDate },
              products: selectedProducts,
              companySettings: companySettings || {},
              showNilai
            });
          }}
          onPrintSeparate={(selectedProducts, showNilai, spkDate) => {
            selectedProducts.forEach((prod, index) => {
              setTimeout(() => {
                generateSpkPdf({
                  invoiceCode: order.order_number || order.id,
                  record: { ...spkInvoiceData, ...order, spk_date: spkDate },
                  products: [prod],
                  companySettings: companySettings || {},
                  showNilai
                });
              }, index * 500);
            });
          }}
        />
      )}

      {/* ASA Stock Deduction Preview Modal */}
      {asaStockModal && (
        <AsaStockDeductionModal
          open={!!asaStockModal}
          onOpenChange={(isOpen) => !isOpen && setAsaStockModal(null)}
          title={asaStockModal.title}
          taskName={asaStockModal.taskName}
          poNumber={asaStockModal.poNumber}
          brandName={asaStockModal.brandName}
          activityType={asaStockModal.activityType}
          previewData={asaStockModal.previewData}
          confirmLabel={asaStockModal.confirmLabel || "Konfirmasi & Selesaikan PO"}
          onConfirm={asaStockModal.onConfirm}
          isLoading={markingDone}
        />
      )}
    </div>
  );
}